/**
 * admin.de — the German (Sie register) translation of the staff-facing admin
 * catalog. `admin.en.ts` is the content source of truth here (verbatim from
 * the views); this file is its Sie translation, typed against
 * `admin.en.ts`'s shape via `CatalogShape` so a missing or extra key here is
 * a compile error, composed into the shared `de` object by `messages.de.ts`.
 */

import type { admin as adminEn } from "./admin.en.ts";
import type { CatalogShape } from "./catalog-shape.ts";

export const admin: CatalogShape<typeof adminEn> = {
	site: {
		mapsJavascript: "Maps JavaScript API",
		placesNew: "Places API (New)",
		geocoding: "Geocoding API",
		staticMaps: "Maps Static API",

		manualAddress:
			"Adressvorschläge benötigen Google Maps. Sie können die Adresse manuell eingeben.",
		configureMaps: "Google Maps einrichten",
		appearance: "Website-Darstellung",
		maps: "Google Maps",
		configure: "Einrichten",
		manage: "Verwalten",
		configured: "Eingerichtet",
		notConfigured: "Nicht eingerichtet",
		checked: "Adresssuche geprüft",
		imageInvalid: "Wählen Sie PNG, JPEG oder WebP unter 2 MB.",
		mapsError:
			"Google konnte die Adresssuche nicht prüfen. Prüfen Sie Abrechnung, aktivierte APIs und Website-Beschränkungen in der Anleitung. Ihr gespeicherter Schlüssel wurde nicht geändert.",
		saveError:
			"Änderungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.",
		testAddress: "Testadresse",
		saveTest: "Speichern & prüfen",
		close: "Schließen",
		searchSharing: "Suche & geteilte Links",
		title: "Titel",
		titleHint: "Leer: Der Name Ihrer Organisation wird verwendet.",
		description: "Beschreibung",
		sharingImage: "Vorschaubild",
		chooseImage: "Bild auswählen",
		siteIcon: "Website-Symbol",
		change: "Ändern",
		browserTab: "Browser-Tab",
		homeScreen: "Home-Bildschirm",
		saveAppearance: "Darstellung speichern",
		searchResult: "Suchergebnis",
		sharedLink: "Geteilter Link",
		preview: "Vorschau",
		browserKey: "Browser-Schlüssel",
		apiKey: "API-Schlüssel",
		guide: "API-Schlüssel erstellen",
		openCloud: "Google Cloud öffnen",
		projectStep:
			"und wählen Sie ein Projekt. Verwenden Sie es für alle folgenden Schritte.",
		openBilling: "Abrechnung öffnen",
		billingStep:
			"und verknüpfen Sie ein Rechnungskonto mit dem Projekt. Google berechnet die Maps-Nutzung.",
		enableStep: "Öffnen Sie jede API und klicken Sie auf Aktivieren:",
		openKeys: "Schlüssel & Anmeldedaten öffnen",
		createStep: "Wählen Sie Anmeldedaten erstellen → API-Schlüssel.",
		websitesStep:
			"Wählen Sie unter Anwendungseinschränkungen Websites. Fügen Sie Ihre Verwaltungswebsite hinzu:",
		restrictStep:
			"Wählen Sie unter API-Einschränkungen Schlüssel einschränken. Wählen Sie dieselben vier APIs aus Schritt 3 und speichern Sie.",
		pasteStep:
			"Kopieren Sie den Schlüssel oben in API-Schlüssel und klicken Sie auf Speichern & prüfen.",
		restriction: "Beschränkung auf Verwaltungswebsite",
		saved: "Änderungen gespeichert",
		siteIconPreviews: "Website-Symbolvorschau",
		changeIcon: "Website-Symbol ändern",
	},
	notifications: {
		title: "Benachrichtigungen",
		setupPasskey: "Passkey einrichten",
	},
	domains: {
		checkingPublicDns: "Öffentliches DNS wird geprüft…",
		dnsRecordAbsent: "Nicht im öffentlichen DNS vorhanden",
		dnsLookupUnavailable: "DNS-Abfrage nicht verfügbar",
		dnsRecordMatches: "Im öffentlichen DNS gefunden",
		dnsRecordNeedsChange: "Im öffentlichen DNS nicht übereinstimmend",
		publicDnsCheckedAt: "Öffentliches DNS geprüft um {{time}}",
		hostingDnsCheckedAt: "Firebase zuletzt geprüft um {{time}}",
		providerUnavailable:
			"Der Anbieter ist nicht erreichbar. Wir versuchen es erneut.",
		shopNotReached:
			"Der Shop ist noch nicht erreichbar. Wir versuchen es erneut.",
		popupBlocked: "Erlauben Sie Pop-ups für diese Seite und prüfen Sie erneut.",
		browserDelayed: "Der Shop hat nicht geantwortet. Versuchen Sie es erneut.",
		addAnother: "Weitere Domain verbinden",
		dnsRouting: "DNS-Verbindung",
		dnsAction: "Aktualisieren Sie Ihre DNS-Einträge",
		ownershipAction: "Bestätigen Sie zuerst Ihre Domain",
		ownershipInstructions:
			"Fügen Sie diesen TXT-Eintrag bei Ihrem DNS-Anbieter hinzu. Er bestätigt die Inhaberschaft, verbindet aber Ihre Website noch nicht.",
		nextDnsInstructions:
			"Danach zeigen wir die Einträge, die Ihre Domain mit dem Shop verbinden. Anschließend prüfen wir das Zertifikat und den Shop.",
		hostInstructions:
			"Ergänzt Ihr Anbieter die DNS-Zone automatisch, geben Sie nur den Teil davor ein. Prüfen Sie die Zone Ihres Anbieters, bevor Sie einen Host-Wert kopieren.",
		hostingInstructions:
			"Aktualisieren Sie diese Einträge bei Ihrem DNS-Anbieter, um Ihren Shop zu verbinden und abzusichern.",
		preparingRecords:
			"Die DNS-Anleitung wird vorbereitet. Wir prüfen automatisch weiter.",
		preparingDnsTitle: "DNS-Anleitung wird vorbereitet",
		waitingSecure:
			"Die DNS-Anleitung ist vollständig. Wir prüfen die sichere Verbindung und den Shopzugang.",
		lastCheck: "Letzte Prüfung: {{status}}",
		autoPrimary:
			"Diese Domain wird Ihre Hauptadresse, sobald alle Verbindungsprüfungen bestanden sind.",
		checkForPrimary:
			"Prüfen Sie die Verbindung, damit diese Domain bei Bereitschaft Ihre Hauptadresse wird.",
		keepPrimary:
			"Ihre aktuelle Hauptadresse bleibt aktiv, bis Sie einen Ersatz wählen.",
		addRecord: "Eintrag hinzufügen",
		checkingConnection: "Verbindung wird geprüft…",
		checkComplete: "Prüfung abgeschlossen",
		checkDelayed: "Die Prüfung läuft noch. Versuchen Sie es gleich erneut.",
		title: "Eigene Domain",
		close: "Schließen",
		manage: "Verwalten",
		connectYour: "Domain verbinden",
		yourDomain: "Ihre Domain",
		connect: "Domain verbinden",
		primaryShop: "Hauptadresse des Shops",
		platformAddress: "Plattformadresse",
		openShop: "Shop öffnen",
		newTab: "öffnet in neuem Tab",
		browserPassed: "Verbunden — im Browser geprüft",
		browserPending: "Verbunden — vor der Auswahl als Hauptadresse prüfen",
		instruction:
			"Öffnen Sie die DNS-Einstellungen bei Ihrem Domainanbieter und legen Sie diese Einträge an. Zum Kopieren auf Name oder Wert klicken.",
		removeRecord: "Diesen Eintrag entfernen",
		type: "Typ",
		name: "Name",
		fullRecordName: "Vollständiger DNS-Name",
		rootHostHint:
			"Wenn Ihre DNS-Zone {{hostname}} ist, geben Sie bei Host ein:",
		hostField: "Host-Feld",
		value: "Wert",
		recordName: "Name des Eintrags",
		recordValue: "Wert des Eintrags",
		checkShop: "Shop prüfen",
		primary: "Hauptadresse",
		makePrimary: "Als Hauptadresse verwenden",
		checkConnection: "Verbindung prüfen",
		disconnect: "Trennen",
		disconnectQuestion: "{{hostname}} trennen?",
		platformStays: "Ihr Shop bleibt über seine Plattformadresse erreichbar.",
		keep: "Domain behalten",
		disconnectDomain: "Domain trennen",
		checks: "Verbindungsprüfung",
		ownership: "Ihre Inhaberschaft",
		verified: "Bestätigt",
		waitingRecord: "Wartet auf Ihren Eintrag",
		secure: "Sichere Verbindung",
		ready: "Bereit",
		preparing: "Wird vorbereitet",
		shopAccess: "Shopzugang",
		configured: "Eingerichtet",
		browserCheck: "Browserprüfung",
		passedHere: "In diesem Browser bestanden",
		notChecked: "Noch nicht geprüft",
		invalid:
			"Geben Sie Ihre Domain ein, etwa shop.example.com — ohne https:// oder eine Seitenadresse.",
		removing: "Die Verbindung wird entfernt",
		cleanupFailed:
			"Die Verbindung konnte nicht entfernt werden. Versuchen Sie es erneut.",
		retryCleanup: "Erneut entfernen",
		disconnecting: "Wird getrennt…",
		unavailable: "Domain nicht verfügbar",
		accessChanged:
			"Ihr Zugriff hat sich geändert. Wenden Sie sich an einen Inhaber der Organisation.",
		adminAttention: "Die Verbindung benötigt Hilfe durch die Administration",
		limit:
			"Die Höchstzahl an Domains ist erreicht. Wenden Sie sich an die Administration.",
		waitingRecords: "Wartet auf Ihre Einträge",
		preparingSecure: "Ihre sichere Verbindung wird vorbereitet",
		finishing: "Die Verbindung wird fertiggestellt",
		connected: "Verbunden",
		loadFailed:
			"Ihre Domains konnten nicht geladen werden. Öffnen Sie die Integrationen erneut.",
		saveFailed:
			"Ihre Anfrage konnte nicht gespeichert werden. Versuchen Sie es erneut.",
		browserFailed:
			"Die Browserprüfung konnte nicht abgeschlossen werden. Versuchen Sie es in einigen Minuten erneut.",
		checkingShop: "Ihr Shop wird geprüft…",
		testSuccess:
			"{{name}}: Ihr Shop funktioniert in diesem Browser. Sie können zu den Einstellungen zurückkehren.",
		testFailed:
			"Die Browserprüfung konnte nicht abgeschlossen werden. Kehren Sie zu den Einstellungen zurück und versuchen Sie es in einigen Minuten erneut.",
		signIn: "Melden Sie sich an, um eine Domain zu verbinden.",
	},
	organizations: {
		label: "Organisation",
		choose: "Organisation wählen",
		reading: "Ihre Organisationen werden geladen",
		openedElsewhere: "{{name}} geöffnet, in einem anderen Fenster gewählt.",
		loadFailed: "Ihre Organisationen konnten nicht geladen werden.",
		switchFailed:
			"Die Organisation konnte nicht gewechselt werden. Versuchen Sie es erneut.",
		switchTo: "Wechseln zu",
		invited: "Sie sind eingeladen",
		invitedTo: "Eingeladen zu",
		search: "Organisationen suchen",
		noMatch: "Keine Organisation passt zu „{{query}}“",
		notConnected: "Diese Shopadresse ist nicht verbunden.",
		notAvailable:
			"Diese Shopadresse ist nicht verfügbar. Versuchen Sie es später erneut.",
		invalid: "Diese Organisationsadresse ist ungültig.",
	},
	invitations: {
		by: "{{invitedBy}} hat Sie als {{roleName}} eingeladen.",
		cost: "Mitglieder sehen Ihren Namen und Ihre E-Mail-Adresse.",
		accept: "Annehmen",
		decline: "Ablehnen",
		declineConfirm: "Ja, ablehnen",
		keep: "Behalten",
		declineCost: "Nur {{invitedBy}} kann Sie erneut einladen.",
		acceptAria: "Annehmen — {{name}}",
		declineAria: "Ablehnen — {{name}}",
		keepAria: "Behalten — {{name}}",
		failed: "Das hat nicht geklappt — nichts hat sich geändert.",
		loadFailed: "Ihre Einladungen konnten nicht geprüft werden.",
		// A founding invitation's consent: the operator's own documents,
		// linked from the sign-in consent's own sentence frames.
		platformTerms: "Plattformbedingungen",
		platformPrivacy: "Datenschutzhinweise für Plattformkonten",
		refused: {
			"no-invitation": {
				said: "Hier wartet keine Einladung für diese Adresse.",
				remedy: "Bitten Sie {{invitedBy}}, Sie erneut einzuladen.",
			},
			"invite-unreadable": {
				said: "Mit dieser Einladung stimmt etwas nicht.",
				remedy: "Bitten Sie {{invitedBy}}, sie zu prüfen.",
			},
			"invite-redeemed": {
				said: "Sie sind schon Mitglied.",
				remedy: "Öffnen Sie sie über Ihre Organisationen.",
			},
			"invite-declined": {
				said: "Sie haben schon abgelehnt.",
				remedy: "Bitten Sie {{invitedBy}}, Sie erneut einzuladen.",
			},
			"invite-revoked": {
				said: "Die Einladung wurde zurückgezogen.",
				remedy: "Bitten Sie {{invitedBy}}, Sie erneut einzuladen.",
			},
			"invite-expired": {
				said: "Die Einladung ist abgelaufen.",
				remedy: "Bitten Sie {{invitedBy}}, Sie erneut einzuladen.",
			},
			"platform-terms": {
				said: "Für die Annahme fehlen die aktuellen Plattformbedingungen.",
				remedy: "Laden Sie die Seite neu, um sie zu lesen.",
			},
		},
	},
	common: {
		confirm: "Bestätigen",
		cancel: "Abbrechen",
		delete: "Löschen",
		save: "Speichern",
		saveChanges: "Änderungen speichern",
		retrySaveShort: "Erneut",
		retrySave: "Fehler · Speichern",
		retryDelete: "Fehler · Löschen",
		retrySend: "Fehler · Senden",
		retry: "Fehler · Wiederholen",
		discard: "Verwerfen",
		close: "Schließen",
		keepEditing: "Weiter bearbeiten",
		confirmCannotUndo: "Diese Aktion kann nicht rückgängig gemacht werden.",
		deleteRefusalCountsUnknown:
			"Die genauen Zahlen ließen sich gerade nicht lesen — aber was das Löschen verhindert, ist trotzdem noch da.",
		status: {
			draft: "Entwurf",
			published: "Veröffentlicht",
			archived: "Archiviert",
		},
	},
	events: {
		list: {
			countSingular: "Event",
			countPlural: "Events",
			addAriaLabel: "Event hinzufügen",
			loadingAriaLabel: "Events werden geladen",
			errorTitle: "Events konnten nicht geladen werden",
			errorBody:
				"Prüfen Sie Ihre Verbindung — die Event-Liste wird automatisch neu geladen.",
			deniedTitle: "Sie haben keinen Zugriff auf die Event-Liste",
			deniedBody:
				"Bitten Sie einen Administrator, Ihnen eine Rolle mit Event-Verwaltungszugriff zuzuweisen.",
			emptyTitle: "Noch keine Events",
			emptyBody:
				"Legen Sie das erste an — ein Name, die Termine und eine Ihrer Locations als Veranstaltungsort.",
			addFirst: "Erstes Event anlegen",
			soldOfCapacity: "{{sold}} von {{capacity}} verkauft",
			soldCaption: "/ {{capacity}} verkauft",
		},
		tile: {
			ariaLabel: "{{name}}, {{status}}",
			ariaLabelInteractive: "{{name}}, {{status}}, Event-Details öffnen",
		},
		form: {
			moveToDayFailed:
				"Am {{day}} werden diese Stunden bei der Zeitumstellung übersprungen — das Einlassfenster kann nicht dorthin verschoben werden. Bitte einen anderen Tag wählen.",
			sectionBasics: "Grunddaten",
			sectionScheduleVenue: "Termin & Veranstaltungsort",
			sectionTicketTypes: "Ticketarten",
			sectionTicketTemplate: "Ticket-Optik",
			ticketTemplateLabel: "Ticket-Vorlage",
			useDefaultTemplate: "Standard-Optik verwenden",
			logoLabel: "Event-Logo",
			logoHint:
				"Optional — die eigene Wortmarke des Events, dargestellt auf Tickets, die das unterstützen. Ohne eigenes Logo wird eine Wortmarke aus dem Eventnamen erzeugt.",
			logoChooseButton: "Logo hochladen",
			logoChangeButton: "Logo ändern",
			logoRemoveButton: "Entfernen",
			logoUnsupportedTypeError:
				"Nur JPG-, PNG- oder WEBP-Bilder werden unterstützt.",
			logoTooLargeError:
				"Diese Bilddatei ist größer als 2 MB — bitte eine kleinere Datei wählen.",
			sectionArtwork: "Motiv",
			sectionStatusLimits: "Status & Limits",
			nameLabel: "Name des Events",
			nameHint: "Das sehen Gäste zuerst — halten Sie es kurz und einprägsam.",
			descriptionLabel: "Beschreibung",
			descriptionHint: "Optional — ein bis zwei Sätze über das Event.",
			schedulePlaceholder: "Wählen Sie den Termin Ihres Events",
			venueLabel: "Veranstaltungsort",
			venuePlaceholder: "Veranstaltungsort wählen",
			addTicketTypeAriaLabel: "Ticketart hinzufügen",
			noTicketTypesYet: "Noch keine Ticketarten.",
			ticketTypeShowsAfter: "Erscheint, wenn „{{name}}“ ausverkauft ist",
			priceDescendsWarning:
				"Günstiger als die vorangehende Ticketart — bitte prüfen, ob das beabsichtigt ist.",
			predecessorClosesAtWarning:
				"Hinweis: Die Ticketart, nach der diese erscheint, hat ein eigenes Verkaufsende — ist das vorbei, erscheint diese hier auch dann, wenn die andere noch nicht ausverkauft ist.",
			noTicketTypesHint:
				"Jedes Event braucht mindestens eine Ticketart — legen Sie die erste an.",
			addFirstTicketType: "Erste Ticketart anlegen",
			draftTicketTypeMeta: "{{kind}} · {{price}} · {{capacity}} Plätze",
			removeTicketTypeAriaLabel: "{{name}} entfernen",
			duplicateTicketTypeAriaLabel: "{{name}} duplizieren",
			reorderTicketTypeAriaLabel: "{{name}} zum Sortieren ziehen",
			reorderGrabbed:
				"{{name}} aufgenommen, Position {{position}} von {{total}}. Mit den Pfeiltasten verschieben, Leertaste zum Ablegen, Escape zum Abbrechen.",
			reorderMoved: "{{name}}, Position {{position}} von {{total}}.",
			reorderDropped:
				"{{name}} an Position {{position}} von {{total}} abgelegt.",
			reorderCancelled: "{{name}} zurück an die ursprüngliche Position.",
			artworkOptionalHint: "Optional, aber das Erste, was auffällt.",
			generateWithAi: "Mit KI erstellen",
			chooseDifferentWayAriaLabel:
				"Andere Methode zum Hinzufügen eines Motivs wählen",
			describePosterLabel: "Motiv beschreiben",
			describePosterPlaceholder:
				"z. B. Neon-Rooftop-Rave, warmes analoges Filmkorn",
			describePosterHint:
				"Eine Stimmung, eine Farbpalette, eine Szene — wenige Worte genügen.",
			generate: "Erstellen",
			generating: "Wird erstellt…",
			posterConceptsLabel: "Motiv-Entwürfe",
			changeArtwork: "Motiv ändern",
			removeArtwork: "Entfernen",
			statusLabel: "Status",
			publishNow: "Dieses Event jetzt veröffentlichen",
			publishNowHint:
				"Deaktiviert bleibt es ein Entwurf, nur für Ihr Team sichtbar — Sie können es jederzeit später veröffentlichen.",
			maxTicketsLabel: "Maximale Tickets pro Bestellung",
			maxTicketsHint:
				"Die höchste Anzahl an Tickets, die ein Käufer in einer Bestellung hinzufügen kann.",
			previewLabel: "Vorschau",
			deleteConfirmTitle: "Dieses Event löschen?",
			publishNoTicketTypesTitle: "Noch keine Ticketarten",
			publishNoTicketTypesMessage:
				"Dieses Event hat noch keine Ticketarten — niemand kann ein Ticket kaufen, bis Sie eine anlegen. Trotzdem veröffentlichen?",
			publishAnyway: "Trotzdem veröffentlichen",
			addTicketTypeFirstCancelText: "Erst eine Ticketart anlegen",
			saveFailed:
				"Das Event konnte nicht gespeichert werden — bitte versuchen Sie es erneut.",
			posterSaveFailed:
				"Das Motiv konnte nicht gespeichert werden — es wird mit dem übrigen Event gespeichert.",
			deleteFailed:
				"Das Event konnte nicht gelöscht werden — bitte versuchen Sie es erneut.",
			deleteRefusalTitle: "Dieses Event kann nicht gelöscht werden",
			deleteRefusalLead:
				"Zu diesem Event gehören weiterhin Bestellungen und Tickets — nach dem Löschen stünden sie ohne Event da:",
			deleteRefusalOrdersOne: "1 Bestellung wurde dafür aufgegeben",
			deleteRefusalOrdersOther:
				"{{count}} Bestellungen wurden dafür aufgegeben",
			deleteRefusalOrdersRemedy:
				"Sind das Testbestellungen, löschen Sie sie einzeln in der Bestellliste — nur so wird eine Bestellung überhaupt entfernt.",
			deleteRefusalTicketsOne: "1 Ticket wurde dafür ausgestellt",
			deleteRefusalTicketsOther: "{{count}} Tickets wurden dafür ausgestellt",
			deleteRefusalTicketsRemedy:
				"Ein Ticket wird nur entfernt, wenn seine Bestellung gelöscht wird — nie für sich allein.",
			deleteRefusalCheckoutsOne: "1 Bestellvorgang läuft noch",
			deleteRefusalCheckoutsOther: "{{count}} Bestellvorgänge laufen noch",
			deleteRefusalCheckoutsRemedy:
				"Ein noch laufender Bestellvorgang endet in ein paar Minuten von selbst — hier ist nichts zu tun.",
			deleteRefusalCampaignsOne: "1 Kampagne läuft noch dafür",
			deleteRefusalCampaignsOther: "{{count}} Kampagnen laufen noch dafür",
			deleteRefusalCampaignsRemedy:
				"Eine noch laufende Kampagne muss zuerst unter »Werbung« beendet werden — sonst gibt Meta weiter Geld für eine Anzeige aus, die auf eine Seite führt, die es nicht mehr gibt.",
			deleteRefusalRemedy:
				"Archivieren Sie es stattdessen: Ein archiviertes Event verschwindet aus dem Shop, während jede Bestellung, jedes Ticket und jede Rechnung erhalten bleibt.",
			// "Stichtag" is avoided throughout: it names a day, and paidBefore is
			// a full instant. futureTypes carries this catalog's Sie register
			// rather than the storefront catalog's Du.
			refund: {
				section: "Rückgabe",
				toggleOn: "Rückgabe ist offen",
				toggleOff: "Keine Rückgabe für dieses Event",
				offHint: "Voreinstellung: keine Rückgabe.",
				windowLabel: "Rückgabefrist",
				windowPlaceholder: "Von wann bis wann",
				paidBeforeToggle: "Nur Bestellungen vor einem festen Zeitpunkt",
				paidBeforeOffHint:
					"Der Kaufzeitpunkt spielt keine Rolle — es zählt allein die Rückgabefrist.",
				paidBeforeLabel: "Gekauft vor",
				paidBeforeHint:
					"Wer zu diesem Zeitpunkt oder später gekauft hat, bekommt nichts zurück.",
				messageLabel: "Was Käufer:innen auf der Rückgabeseite lesen",
				messageHintDe: "Deutsch",
				messageHintEn: "English",
				messagePlaceholder:
					"z. B. Rückgaben nehmen wir bis eine Woche vor dem Konzert entgegen.",
				typesLabel: "Welche Ticketarten werden zurückgenommen?",
				typesNotRefundable: "Wird nicht zurückgenommen: {{names}}",
				typesNoneSelected:
					"Nichts ausgewählt — jede Rückgabeanfrage wird abgelehnt.",
				typesAllSelected: "Alle Ticketarten werden zurückgenommen.",
				futureTypes:
					"Neue Ticketarten sind zunächst nicht dabei — legen Sie später eine an, wählen Sie sie hier aus.",
			},
			validation: {
				nameRequired: "Name ist erforderlich.",
				venueRequired: "Wählen Sie eine Ihrer Locations als Veranstaltungsort.",
				datesRequired: "Wählen Sie Start- und Enddatum.",
				invalidTime:
					"{{time}} gibt es an diesem Tag in der Zeitzone des Veranstaltungsorts ({{tz}}) nicht — die Uhren überspringen diese Zeit, wenn sie im Frühjahr vorgestellt werden. Wählen Sie eine andere Uhrzeit.",
				endAfterStart: "Das Event muss nach seinem Start enden.",
				maxTicketsMin:
					"„Maximale Tickets pro Bestellung“ muss mindestens 1 sein.",
				refundWindowRequired:
					"Wählen Sie, wann die Rückgabe öffnet und schließt.",
				refundEndAfterStart: "Die Rückgabe muss nach ihrem Beginn schließen.",
				refundPaidBeforeRequired:
					"Wählen Sie den Kaufzeitpunkt, oder schalten Sie diese Option aus.",
			},
			generateArtworkEmptyPrompt: "Beschreiben Sie zuerst das Motiv.",
			generateArtworkRateLimited:
				"Zu viele Anfragen in dieser Stunde — bitte später erneut versuchen.",
			generateArtworkUnavailable:
				"Das Motiv konnte gerade nicht erstellt werden — bitte versuchen Sie es erneut.",
			generateArtworkError:
				"Bei der Motiv-Erstellung ist etwas schiefgelaufen — bitte versuchen Sie es erneut.",
			overlayEditTicketType: "Ticketart bearbeiten",
			overlayDuplicateTicketType: "Ticketart duplizieren",
			overlayNewTicketType: "Neue Ticketart",
		},
		localize: {
			section: "Sprachen",
			sectionHint:
				"Schreiben Sie die Beschreibung und Ihre Ticketarten einmal, in der Sprache, in der Sie denken. Hier entsteht daraus auf einen Schlag ein Entwurf in der anderen Sprache — Sie lesen jede Fassung, korrigieren, was Sie möchten, und erst dann wird sie gespeichert. Für ein einzelnes Feld nutzen Sie stattdessen das Übersetzen-Symbol neben diesem Feld.",
			saveFirstHint:
				"Speichern Sie Ihre Änderungen zuerst — hier wird die Beschreibung gelesen, wie sie aktuell am Event steht.",
			openButton: "Sprachen",
			overlayAriaLabel: "Sprachen für dieses Event",
			title: "Sprachen",
			intro:
				"Das geht einmal für das ganze Event heraus — die Beschreibung und jede Ticketart zusammen — und kommt in jeder Sprache zurück, die der Shop bedient. Gespeichert wird nichts, bevor Sie es übernehmen.",
			nothingToTranslate:
				"Es gibt noch nichts zu übersetzen — schreiben Sie eine Beschreibung oder legen Sie zuerst eine Ticketart an.",
			subjectDescription: "Event-Beschreibung",
			subjectTicketType: "Ticketart",
			draftButton: "Übersetzen",
			draftingStatus: "Die Texte dieses Events werden übersetzt",
			draftingNote:
				"Ihre Worte werden in jeder Sprache geschrieben. Das dauert einen Moment.",
			reviewIntro:
				"Lesen Sie jede Fassung und korrigieren Sie, was falsch klingt — genau diese Worte sehen Ihre Käufer. Gespeichert wird nichts, bevor Sie übernehmen.",
			localeDe: "Deutsch",
			localeEn: "Englisch",
			provenanceYours: "Ihre eigenen Worte",
			provenanceTranslated: "Übersetzt",
			nameLabel: "Name",
			descriptionLabel: "Beschreibung",
			acceptButton: "Übernehmen",
			discardButton: "Verwerfen",
			acceptSuccess: "Alle Sprachen sind gespeichert.",
			acceptFailed:
				"Die Übersetzungen konnten nicht gespeichert werden — es wurde nichts geändert. Bitte erneut versuchen.",
			draftFailed:
				"Die Übersetzung ist nicht angekommen — bitte erneut versuchen.",
			rateLimited:
				"Zu viele Übersetzungen in dieser Stunde — bitte später erneut versuchen.",
			unavailable:
				"Übersetzungen sind gerade nicht verfügbar — bitte gleich erneut versuchen.",
			triggerAriaLabel: "Sprachen — {{field}}",
			originalLabel: "Original",
			originalSeparatorNote:
				"Eine Zeile, die nur // enthält, trennt das Original an dieser Stelle. Vergleichen Sie die Fassungen daran, was sie sagen, nicht daran, wie lang sie sind — das Original kann dasselbe mehrfach sagen, eine Fassung sagt es einmal.",
			autoNote:
				"Wird automatisch aus dem Original geschrieben. Ändern Sie das Original, werden alle Fassungen hier neu geschrieben — Korrekturen eingeschlossen.",
			pendingNote: "Die anderen Sprachen werden gerade geschrieben.",
			readyNote: "Die Übersetzungen sind da.",
			emptyPlaceholder: "Noch keine eigene Fassung — Leser sehen das Original.",
			tooLong: "Höchstens {{max}} Zeichen — aktuell {{count}}.",
			fieldSaveFirstHint:
				"Speichern Sie zuerst dieses Feld — die Fassungen werden aus dem Text geschrieben, wie er gerade dasteht.",
			fieldNothingStoredHint:
				"Noch nichts zu übersetzen — schreiben Sie dieses Feld und speichern Sie es zuerst.",
			fieldTranslateButton: "Neu übersetzen",
			fieldTranslateRequested:
				"Eine neue Übersetzung ist angefragt — die neuen Fassungen erscheinen gleich hier.",
			fieldTranslateRequestFailed:
				"Die Anfrage konnte nicht gesendet werden — bitte erneut versuchen.",
			fieldTranslateFailedHint:
				"Die anderen Sprachen wurden nicht geschrieben — Käufer sehen diesen Text vorerst in Ihrer eigenen Sprache. Öffnen Sie die Sprachen und fragen Sie einen neuen Versuch an.",
			fieldStored: "Die Fassungen dieses Feldes sind gespeichert.",
			fieldStoreFailed:
				"Diese Fassung konnte nicht gespeichert werden — es wurde nichts geändert. Bitte erneut versuchen.",
		},
		console: {
			tabOverview: "Überblick",
			tabOrders: "Bestellungen",
			tabGuests: "Gäste",
			tabEdit: "Bearbeiten",
			colDate: "Datum",
			colBuyer: "Käufer",
			colTickets: "Tickets",
			colAmount: "Betrag",
			colStatus: "Status",
			colCheckedIn: "Eingecheckt",
			statusPaid: "Bezahlt",
			statusAuthorised: "Noch nicht abgebucht",
			statusRefunded: "Erstattet",
			statusCancelled: "Storniert",
			guestSuffix: "Gast",
			deliveryPending: "Gesendet",
			deliveryDelivered: "Zugestellt",
			deliveryOpened: "Geöffnet",
			deliveryBounced: "Nicht zugestellt",
			detailTicketsLabel: "Tickets in dieser Bestellung",
			detailNoTickets: "Noch keine Tickets ausgestellt",
			ticketCheckedIn: "Eingecheckt",
			ticketNotYet: "Noch nicht eingecheckt",
			ticketCancelled: "Storniert",
			overviewNoTypesTitle: "Noch keine Ticketarten",
			overviewNoTypesBody: "Füge unter Bearbeiten eine hinzu, um zu verkaufen.",
			navDashboard: "Übersicht",
			navOrders: "Bestellungen",
			navEdit: "Bearbeiten",
			navAnalytics: "Analytics",
			checkinSettingsAriaLabel: "Einlass-Einstellungen",
			checkinManualLookupHeading: "Gast nach Namen suchen",
			checkinEscalateBody:
				"Frag eine Aufsicht — ihr Konto kann diesen Gast nach Namen suchen.",
			back: "Zurück zu den Events",
			eventNotFoundTitle: "Event nicht gefunden",
			eyebrowOnSale: "Im Verkauf",
			eyebrowPresent: "Findet gerade statt",
			eyebrowAfter: "Vergangenes Event",
			noTicketTypesConfigured: "Noch keine Ticketarten eingerichtet",
			noAccessBody:
				"Bitten Sie einen Administrator, Ihnen eine Rolle mit Zugriff auf Verkaufsberichte zuzuweisen.",
			noTicketTypesTitle: "Noch keine Ticketarten",
			noTicketTypesBody:
				"Legen Sie unter Bearbeiten eine an, um mit dem Verkauf zu starten.",
			goToEditButton: "Zu Bearbeiten",
			exceptionsOpenAria: "Diese Bestellungen öffnen",
			exceptionRefunded: "{{count}} erstattet",
			exceptionAuthorised: "{{count}} noch nicht abgebucht",
			exceptionCancelled: "{{count}} bei einer bezahlten Bestellung storniert",
			grossTakenLabel: "Bruttoeinnahmen",
			youKeepLabel: "Das bleibt Ihnen",
			youKeepAtMost: "höchstens {{amount}}",
			atMost: "höchstens",
			youKeepAfter: "nach {{vat}} MwSt. und {{fees}} Gebühren",
			youKeepAfterAtLeast:
				"nach {{vat}} MwSt. · Gebühren für {{reported}} von {{total}} Bestellungen gemeldet",
			grossTakenIncludes:
				"Enthält {{vat}} MwSt. · Transaktionsgebühren noch nicht gemeldet",
			youKeepBreakdownAria: "Aufschlüsselung anzeigen",
			grossTakenBreakdownAria: "Abzüge anzeigen",
			authorisedSuffix:
				"{{amount}} von der Bank genehmigt, aber noch nicht abgebucht",
			last7dLabel: "Letzte 7 Tage",
			momentumUp: "{{count}} mehr als in der Vorwoche",
			momentumDown: "{{count}} weniger als in der Vorwoche",
			momentumFlat: "Gleich wie in der Vorwoche",
			refundedCancelledLabel: "Erstattet / storniert",
			scheduleDaysToGo: "in {{count}} Tagen",
			presentLabel: "Eingecheckt",
			attendanceLabel: "Besucher",
			checkedInFraction: "{{redeemed}} / {{valid}}",
			soldFraction: "{{sold}} / {{capacity}}",
			soldLabel: "Verkauft",
			ticketsIssuedLabel: "Tickets ausgestellt",
			ticketsIssuedBreakdownAria:
				"Aufschlüsselung der ausgestellten Tickets anzeigen",
			ticketsIssuedBreakdownDialogAria:
				"Aufschlüsselung der ausgestellten Tickets",
			issuedToGuestsLabel: "An Gäste ausgestellt",
			soldScalar: "{{count}} verkauft",
			paceUp: "Verkauft sich schneller als letzte Woche",
			paceDown: "Verkauft sich langsamer als letzte Woche",
			arrivals15Label: "in den letzten 15 Min. angekommen",
			attendanceFinal: "Kamen durch die Tür",
			arrivals15: "{{count}} in den letzten 15 Min. angekommen",
			noCheckInsYet: "Noch keine Check-ins.",
			publicShopEmptyTitle: "Nichts im öffentlichen Verkauf",
			publicShopEmptyBody:
				"Keine Ticketart ist für alle mit dem Link freigegeben, im Shop gibt es also nichts zu kaufen. Öffnen Sie unten eine Ticketart und wählen Sie „Alle mit dem Link“.",
			typesTitle: "Ticketarten",
			todayLabel: "Heute",
			colType: "Art",
			colPrice: "Preis",
			colRevenue: "Umsatz",
			colGuest: "Gäste",
			noOrdersYet: "Noch keine Bestellungen",
			sellThroughAria: "{{sold}} von {{capacity}} verkauft",
			headlineOpenAria:
				"{{sold}} von {{capacity}} verkauft — Ticketarten öffnen",
			headlineTypeAria: "{{name}} {{sold}} von {{capacity}}",
			closeProgrammeAria: "Ticketarten schließen",
			loadingFigures: "Zahlen zu dieser Veranstaltung werden geladen",
			ordersLoadFailed:
				"Bestellungen konnten nicht geladen werden — laden Sie die Seite neu.",
			notLive:
				"Verbindung wird wiederhergestellt — dies ist möglicherweise nicht aktuell.",
			ticketsLoadFailed:
				"Tickets konnten nicht geladen werden — laden Sie die Seite neu.",
			capacityAria: "{{capacity}} Plätze",
			soldOutLabel: "Ausverkauft",
			searchLabel: "Nach Name, E-Mail oder Ticketnummer suchen",
			searchAriaLabel:
				"Bestellungen nach Name, E-Mail oder Ticketnummer suchen",
			searchBuyerLabel: "Nach Name oder E-Mail suchen",
			searchBuyerAriaLabel: "Bestellungen nach Name oder E-Mail durchsuchen",
			facetGuestLabel: "Gast",
			facetSaleLabel: "Verkauf",
			facetRefundedLabel: "Erstattet",
			dateRangeLabel: "Zeitraum",
			filtersButtonLabel: "Filter",
			filtersButtonAriaActive: "Filter — {{count}} aktiv",
			filtersButtonAriaInactive: "Filter",
			noMatchTitle: "Keine Treffer",
			noMatchBody: "Keine Bestellung entspricht dieser Suche.",
			revenueBreakdown: {
				dialogAriaLabel: "Was bleibt, aufgeschlüsselt",
				grossLabel: "Brutto eingenommen",
				grossExplained:
					"Was Käufer für Tickets bezahlt haben, abzüglich Erstattungen",
				vatLabel: "Geschuldete MwSt.",
				vatSingleRate: "{{rate}} % — bereits in den Ticketpreisen enthalten",
				vatMixedRate:
					"Bereits in den Ticketpreisen enthalten — der Satz unterscheidet sich je nach Ticketart",
				feesLabel: "Transaktionsgebühren",
				feesExplained:
					"Was der Zahlungsanbieter für die Abwicklung dieser Zahlungen einbehalten hat",
				feesAtLeast: "bisher mindestens so viel",
				feesNotReported:
					"Der Zahlungsanbieter hat die Gebühren noch nicht übermittelt",
				keepLabel: "Das bleibt Ihnen",
				keepAtMost: "höchstens {{amount}}",
				keepPending:
					"Bei {{count}} dieser Bestellungen fehlen die Gebühren noch, dieser Betrag sinkt also noch.",
				unattributableNote:
					"Erstattungen bei {{count}} dieser Bestellungen konnten keinen Ticketpositionen zugeordnet werden und fehlen deshalb in allen Beträgen.",
			},
			orderDetail: {
				ticketsPending:
					"Die Tickets für diese Bestellung wurden noch nicht ausgestellt.",
				ticketsCancelled:
					"Diese Bestellung wurde storniert, bevor Tickets ausgestellt wurden.",
				invoiceHeading: "Rechnung",
				invoiceOpen: "Rechnung öffnen",
				invoiceNone:
					"Keine Rechnung — diese Bestellung wurde storniert, bevor ein Ticket ausgestellt wurde.",
				invoicePending:
					"Noch keine Rechnung — diese Bestellung hat ihre Rechnungsnummer noch nicht bekommen.",
				transactionFee: "{{amount}} Transaktionsgebühr",
				transactionFeeNotReturned:
					"{{amount}} Transaktionsgebühr — bei Erstattung nicht zurückerstattet",
				transactionFeePending: "Transaktionsgebühr noch nicht gemeldet",
				feeSuffix: " · {{fee}} Gebühr",
				ticketCountOne: "{{count}} Ticket",
				ticketCountMany: "{{count}} Tickets",
				checkinLabel: "Einchecken",
				undoCheckinLabel: "Check-in rückgängig",
				undoCancel: "Behalten",
				undoConfirmTitle: "Diesen Check-in rückgängig machen?",
				undoConfirmBody:
					"{{typeName}} wird wieder gültig und kann ein zweites Mal eingelassen werden. An welchem Eingang und wann es eingecheckt wurde, wird endgültig gelöscht.",
				sendTicketsAria: "Tickets senden",
				sendTicketsNoPermissionAria:
					"Sie haben keinen Zugriff auf die Verkaufsberichte",
				sendTicketsAddressLabel: "E-Mail-Adresse",
				sendTicketsInvalidEmail: "Bitte eine gültige E-Mail-Adresse eingeben.",
				sendTicketsNoAddress:
					"Keine E-Mail-Adresse hinterlegt — diese Bestellung kann an niemanden gesendet werden.",
				sendTicketsSubmit: "Tickets senden",
				ticketImageDownloadStarted: "Ticketbild-Download gestartet.",
				sendTicketsFailed:
					"E-Mail konnte nicht eingeplant werden. Erneut versuchen.",
				sendTicketsAddressUpdatedSendFailed:
					"E-Mail korrigiert. Senden fehlgeschlagen; erneut senden.",
				sendTicketsCorrectSubmit: "Korrigieren & senden",
				sendTicketsCancel: "Abbrechen",
				sendTicketsConfirmTitle: "E-Mail dieser Bestellung korrigieren?",
				sendTicketsConfirmBody:
					"Die Tickets gehen an {{newEmail}}. {{oldEmail}} erreicht diese Bestellung nicht mehr.",
				sendTicketsConfirmSubmit: "Korrigieren & senden",
				sendTicketsConfirmCancel: "Adresse behalten",
				copyLinkAria: "Den Bestelllink kopieren",
				noShareableLinkAria:
					"Noch kein Link zum Teilen — diese Bestellung lässt sich noch nicht per Link öffnen",
				shareAria: "Bestelllink teilen",
				ticketCountSingular: "Ticket",
				ticketCountPlural: "Tickets",
				purchased: "Gekauft {{relative}}",
				issued: "Ausgestellt {{relative}}",
				guestTicketSuffix: " · Gästeticket",
				testOrder: "Testbestellung",
				toastEmailQueued: "E-Mail wird in Kürze verschickt.",
				toastLinkCopied: "Bestelllink kopiert.",
				toastLinkCopyFailed: "Der Bestelllink konnte nicht kopiert werden.",
				toastShareFailed: "Die Bestellung konnte nicht geteilt werden.",
				toastEmailUpdated: "E-Mail aktualisiert — wird an {{email}} gesendet.",
				refundAria: "Tickets dieser Bestellung erstatten",
				refundExitAria: "Erstattung abbrechen",
				ticketRefunded: "Erstattet",
				ticketRefundedAmount: "{{amount}} erstattet",
				ticketRefundAlsoCoversSingular: "zusammen mit 1 weiteren Ticket",
				ticketRefundAlsoCoversPlural: "zusammen mit {{count}} weiteren Tickets",
				ticketRefundConfirming: "Wartet auf die Bestätigung des Anbieters",
				ticketRefundUnderWay: "Erstattung läuft",
				ticketRefundUnderWayBody:
					"Es ist noch nichts zurückgegangen, und dieses Ticket wird am Einlass weiterhin eingelassen",
				ticketRefundRefusedBody:
					"Der Anbieter hat bisher abgelehnt — siehe den Erstattungsvorgang unten",
				ticketRefundFailed: "Erstattung fehlgeschlagen",
				ticketRefundFailedStillAdmits:
					"Es ist kein Geld zurückgegangen, und dieses Ticket wird am Einlass weiterhin eingelassen",
				ticketRefundFailedBody:
					"Es ist kein Geld zurückgegangen — siehe den Erstattungsvorgang unten",
				ticketCancelledNoRefund:
					"Kein Erstattungsvorgang vorhanden — es wurde nichts zurückgezahlt",
				ticketRefundChecking: "Der Erstattungsvorgang wird geprüft …",
				refundRefusalRedeemed: "Am Einlass eingecheckt — nicht erstattbar",
				refundRefusalCancelled: "Storniert — nicht mehr erstattbar",
				refundRefusalClaimed:
					"Dieses Ticket gehört bereits zu einer laufenden Erstattung",
				refundRefusalUnpriced:
					"Kein Preis pro Ticket — diese Bestellung und ihre Tickets passen nicht zusammen",
				refundTicketAria:
					"{{typeName}} {{index}} von {{count}} erstatten — {{amount}}",
				refundTicketRevokeAria:
					"{{typeName}} {{index}} von {{count}} entwerten — es fließt kein Geld",
				refundRecordAria:
					"Erstattungsvorgang für {{typeName}} {{index}} von {{count}} öffnen",
				refundTicketsUnreadable:
					"Dieses Konto kann die Tickets dieser Veranstaltung nicht sehen. Deshalb lässt sich nicht anzeigen, welche Tickets eine Erstattung entwerten und wie viele Plätze zurückkommen würden. Dafür fehlt die Einlass-Berechtigung.",
				refundLedgerPending: "Laufende Erstattungen werden geprüft …",
				refundEmpty: "Wählen Sie die Tickets zur Erstattung",
				refundSelectAll: "Alle auswählen",
				refundSelectNone: "Auswahl aufheben",
				refundSubmit: "{{amount}} erstatten",
				refundRevokeSubmit: "Ausgewählte Tickets entwerten",
				refundConfirmTitle: "{{amount}} erstatten?",
				refundConfirmBodySingular:
					"{{amount}} gehen an {{buyer}} zurück. 1 Ticket wird am Einlass nicht mehr eingelassen, und 1 Platz geht zurück in den Verkauf. Das lässt sich hier nicht rückgängig machen.",
				refundConfirmBodyPlural:
					"{{amount}} gehen an {{buyer}} zurück. {{count}} Tickets werden am Einlass nicht mehr eingelassen, und {{count}} Plätze gehen zurück in den Verkauf. Das lässt sich hier nicht rückgängig machen.",
				refundRevokeConfirmTitle: "Ausgewählte Tickets entwerten?",
				refundRevokeConfirmBodySingular:
					"Es fließt kein Geld — {{buyer}} hat für dieses Ticket nichts bezahlt. 1 Ticket wird am Einlass nicht mehr eingelassen, und 1 Platz geht zurück in den Verkauf. Das lässt sich hier nicht rückgängig machen.",
				refundRevokeConfirmBodyPlural:
					"Es fließt kein Geld — {{buyer}} hat für diese Tickets nichts bezahlt. {{count}} Tickets werden am Einlass nicht mehr eingelassen, und {{count}} Plätze gehen zurück in den Verkauf. Das lässt sich hier nicht rückgängig machen.",
				purgeAria: "Diese Bestellung löschen",
				purgeConfirmTitle: "Diese Bestellung endgültig löschen?",
				purgeConfirmBodySingular:
					"Der Vorgang selbst verschwindet, nicht nur das Ticket: Diese Bestellung, ihr 1 Ticket, ihr Link und ihre gespeicherten Dateien werden vollständig gelöscht — der Platz geht zurück in den Verkauf, und ein darauf eingelöster Gutschein lässt sich wieder verwenden. Die Rechnungsnummer bleibt vergeben, und nichts davon lässt sich hier wiederherstellen.",
				purgeConfirmBodyPlural:
					"Der Vorgang selbst verschwindet, nicht nur die Tickets: Diese Bestellung, ihre {{count}} Tickets, ihr Link und ihre gespeicherten Dateien werden vollständig gelöscht — die Plätze gehen zurück in den Verkauf, und ein darauf eingelöster Gutschein lässt sich wieder verwenden. Die Rechnungsnummer bleibt vergeben, und nichts davon lässt sich hier wiederherstellen.",
				purgeConfirmCta: "Endgültig löschen",
				purgeCancel: "Behalten",
				purgeToast:
					"Bestellung gelöscht — {{count}} Tickets und ihre Plätze sind weg.",
				purgeFailed:
					"Die Bestellung konnte nicht gelöscht werden. Möglicherweise ist sie bereits weg, oder es ist eine bezahlte Bestellung — die lässt sich nicht löschen.",
				refundToast:
					"Erstattung gestartet — sobald das Geld zurückgeht, gehen die Plätze zurück in den Verkauf.",
				refundFailed:
					"Die Erstattung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
			},
			purgeAllOrders: {
				openAria: "Alle Testbestellungen dieser Veranstaltung löschen",
				confirmTitle: "Alle {{count}} Testbestellungen endgültig löschen?",
				confirmBody:
					"Alle {{count}} Testbestellungen dieser Veranstaltung, ihre Tickets, ihre Links und ihre gespeicherten Dateien werden vollständig gelöscht — die Plätze gehen zurück in den Verkauf, und darauf eingelöste Gutscheine lassen sich wieder verwenden. Echte Bestellungen bleiben unberührt. Die Rechnungsnummern bleiben vergeben, und nichts davon lässt sich hier wiederherstellen.",
				confirmCta: "Alle {{count}} löschen",
				cancel: "Behalten",
				toast: "{{count}} Testbestellungen gelöscht.",
				partialToast:
					"{{count}} Testbestellungen gelöscht, {{failed}} nicht — sie sind möglicherweise bereits weg oder keine Testbestellungen.",
			},
			orderRefunds: {
				title: "Erstattungen",
				waitingSingular:
					"1 Erstattung dieser Bestellung wartet auf eine Person.",
				waitingPlural:
					"{{count}} Erstattungen dieser Bestellung warten auf eine Person.",
				settled: "{{amount}} sind am {{date}} zurückgegangen",
				settledTicketsSingular: "1 Ticket",
				settledTicketsPlural: "{{count}} Tickets",
				pending:
					"{{amount}} sind zurückgegangen — Revolut muss es noch bestätigen",
				claiming: "{{amount}} werden gerade ausgezahlt",
				correction: "Rechnungskorrektur {{number}}",
				originCustomer: "Von der Kundschaft zurückgegeben",
				originPsp: "Im Revolut-Dashboard ausgelöst",
				retryingHeadline:
					"{{amount}} sind noch nicht zurück — Revolut lehnt es weiterhin ab",
				retryingLadder:
					"Das System fragt alle 15 Minuten erneut und gibt nach zehn Versuchen auf. An dieser Bestellung hat sich bisher nichts geändert.",
				retryingInsufficientFunds:
					"Revolut sagt, es sei nicht genug Geld da, um diese Erstattung auszuzahlen. Eine Erstattung wird von Ihrem eigenen Revolut-Konto ausgezahlt, und dessen Kontostand ist hier nicht zu sehen — schauen Sie im Revolut-Dashboard nach. Ist er zu niedrig, laden Sie das Konto auf: Das System fragt alle 15 Minuten erneut, insgesamt zehnmal, und der erste Versuch nach dem Aufladen zahlt von allein aus. Ist er hoch genug, wird diese Erstattung aus einem anderen Grund abgelehnt und braucht eine Person.",
				failedProviderRefused:
					"Revolut hat diese Erstattung endgültig abgelehnt.",
				failedAttemptsExhausted:
					"Das System hat zehnmal gefragt und aufgehört.",
				failedAttemptsExhaustedLanded:
					"Die Versuche waren aufgebraucht, aber das Geld war schon zurück.",
				failedOverpaid:
					"Es ist mehr Geld zurückgegangen, als diese Bestellung verbuchen kann.",
				failedRedeemedAfterClaim:
					"Ein Ticket dieser Erstattung wurde am Einlass eingecheckt, nachdem die Erstattung begonnen hatte.",
				failedHeadroomLost:
					"Diese Erstattung passt nicht mehr zu dem, was die Bestellung noch zurückgeben kann.",
				failedSettleRefused:
					"Das Geld ist zurückgegangen, ließ sich aber nirgends verbuchen.",
				failedUnsettledPastBound:
					"Die Summen von Revolut zeigen diese Erstattung auch einen Tag später nicht.",
				moneyNothingMoved:
					"Nichts wurde verbucht: {{amount}} stehen weiterhin auf der Summe dieser Bestellung, die Tickets kommen am Einlass weiterhin durch, und die Plätze sind weiterhin weg.",
				moneyCheckDashboard:
					"Ein früherer Versuch kann trotzdem ausgezahlt haben. Schauen Sie zuerst im Revolut-Dashboard nach, bevor Sie etwas tun.",
				moneyDoorRecordStands:
					"Nichts wurde verbucht, und der Einlass behält recht — jemand ist mit einem Ticket hereingekommen, das diese Erstattung entwerten wollte. Das braucht eine Entscheidung, keinen weiteren Versuch.",
				moneySettleRefused:
					"{{amount}} haben Revolut verlassen, und diese Bestellung zeigt davon nichts: Summe, Tickets und Plätze sind unverändert.",
				moneyOverpaid:
					"{{amount}} sind verbucht — diese Tickets sind entwertet und ihre Plätze zurück im Verkauf. Weitere {{unrecorded}} haben Revolut verlassen und stehen in keiner Summe.",
				moneyUnsettled:
					"Die Bücher zählen sie bereits: {{amount}} sind von der Summe dieser Bestellung abgezogen, die Tickets sind entwertet und die Plätze zurück. Ist das Geld nie geflossen, stimmt keines der drei.",
				moneyLanded:
					"{{amount}} sind tatsächlich zurückgegangen, und Tickets und Plätze sind gefolgt. Zu klären bleibt nur noch der Status dieses Eintrags.",
				holdsTicketsSingular:
					"Dieser Eintrag hält sein 1 Ticket fest — solange er steht, erreicht es keine neue Erstattung.",
				holdsTicketsPlural:
					"Dieser Eintrag hält seine {{count}} Tickets fest — solange er steht, erreichen sie keine neue Erstattung.",
				clearedByHand:
					"Einen Eintrag zu klären ist eine Korrektur der Bücher von Hand — die Reihenfolge dafür steht im Erstattungs-Runbook.",
				failedUnknown:
					"Diese Erstattung ist stehen geblieben und hat nicht gesagt, warum.",
				providerSaid: "Revolut sagt:",
				copyReference: "Revolut-Referenz kopieren",
				referenceCopied:
					"Referenz kopiert — damit im Revolut-Dashboard suchen.",
				referenceCopyFailed: "Die Referenz konnte nicht kopiert werden.",
			},
			refundAllOrders: {
				openAria: "Alle bezahlten Bestellungen dieser Veranstaltung erstatten",
				dialogAriaLabel: "Alle bezahlten Bestellungen erstatten",
				title: "Alle {{count}} bezahlten Bestellungen erstatten?",
				moneyLine: "{{amount}} gehen an die Käuferinnen und Käufer zurück",
				ticketsLine:
					"{{count}} Tickets werden am Einlass nicht mehr eingelassen",
				seatsLine: "{{count}} Plätze gehen zurück in den Verkauf",
				staysPublished:
					"Die Veranstaltung bleibt veröffentlicht, und diese Plätze können wieder gekauft werden, sobald sie zurückkommen. Wenn die Veranstaltung ausfällt, archivieren Sie sie zusätzlich — diese Erstattung erledigt das nicht für Sie.",
				redeemedWarning:
					"Bei {{count}} dieser Bestellungen wurde bereits ein Ticket am Einlass eingecheckt. Diese Bestellungen bleiben unberührt — eine Erstattung würde den Nachweis über den Einlass löschen — ihr Geld und ihre Plätze sind oben deshalb nicht mitgezählt.",
				ticketsUnreadable:
					"Dieses Konto kann die Tickets dieser Veranstaltung nicht sehen. Deshalb lässt sich nicht anzeigen, wie viele Tickets nicht mehr eingelassen würden und wie viele Plätze zurückkämen. Dafür fehlt die Einlass-Berechtigung.",
				submit: "Alle {{count}} Bestellungen erstatten",
				sending: "Wird gesendet …",
				sendingAria: "Die Erstattungen werden gesendet",
				failed:
					"Die Erstattung konnte nicht gestartet werden. Es wurde nichts gesendet — die Bestellungen bleiben unberührt.",
				retry: "Erneut versuchen",
				progressAria: "Bearbeitete Bestellungen in dieser Erstattung",
				progressLabel: "{{done}} von {{count}} Bestellungen bearbeitet",
				progressFailed:
					"{{count}} abgelehnt — prüfen Sie die Erstattungszeilen",
				progressSettled: "Jede Bestellung wurde bearbeitet.",
				progressUnwatched:
					"Dieses Konto kann den Fortschritt dieser Erstattung nicht weiter verfolgen — sie läuft im Hintergrund weiter, aber die Zahlen oben aktualisieren sich nicht mehr.",
			},
		},
	},
	ticketTypes: {
		form: {
			kindTicket: "Ticket",
			kindAddon: "Zusatzoption",
			whatIsThisLabel: "Was ist das?",
			typeHint:
				"Ein Ticket berechtigt zum Einlass; eine Zusatzoption ist ein Extra, etwa Parkplatz oder ein Getränk.",
			nameLabel: "Wie heißt sie?",
			nameHint:
				"Das sehen Käuferinnen und Käufer an der Kasse — z. B. „Frühbucher“ oder „Tageskarte“.",
			priceLabel: "Preis pro Ticket (€)",
			vatLabel: "USt. %",
			vatHint:
				"Meist 7 bei Eventtickets. Nicht alles bekommt den ermäßigten Satz — Workshops zum Beispiel oft nicht.",
			capacityLabel: "Wie viele davon können Sie verkaufen?",
			capacityHint: "Sind so viele verkauft, ist sie ausverkauft.",
			claimableByLabel: "Wer bekommt diese Ticketart?",
			claimableByBuyer: "Alle mit dem Link",
			claimableByStaff: "Nur Ihr Team",
			claimableByBuyerHint:
				"Sie steht im öffentlichen Shop — alle mit dem Event-Link können sie kaufen.",
			claimableByBuyerFreeHint:
				"Sie steht für 0 € im öffentlichen Shop — alle mit dem Event-Link können sie ohne Bezahlung beanspruchen, bis die Kapazität oben erschöpft ist.",
			claimableByStaffHint:
				"Sie erscheint nie im öffentlichen Shop. Nur Ihr Team kann sie über die Gästeliste ausstellen.",
			claimableByStaffFreeHint:
				"Sie erscheint nie im öffentlichen Shop — bei einer kostenlosen Ticketart bleibt das so, solange Sie nicht „Alle mit dem Link“ wählen. Nur Ihr Team kann sie über die Gästeliste ausstellen.",
			claimableByCode: "Nur mit Code",
			claimableByCodeHint:
				"Sie erscheint erst im Shop, wenn jemand einen Code eingibt, der sie freischaltet — vorher sieht sie niemand.",
			claimableByCodeUnlockedBy: "Freigeschaltet von: {{codes}}.",
			claimableByCodeStranded:
				"Zurzeit schaltet kein Code dieser Veranstaltung sie frei — niemand kann sie kaufen.",
			claimableByCodeCreate: "Code anlegen, der sie freischaltet",
			verifyAtDoorLabel: "Am Einlass",
			verifyAtDoorScan: "Scannen und durchlassen",
			verifyAtDoorCheck: "Erst persönlich prüfen",
			verifyAtDoorScanHint:
				"Das Scannen des Tickets genügt — der Gast geht direkt hinein, und am Einlass prüft niemand etwas anderes.",
			verifyAtDoorCheckHint:
				"Jedes Ticket dieser Art wartet am Einlass, bis jemand aus Ihrem Team es bestätigt — auch wenn der Scanner auf Automatisch steht.",
			doorNoteLabel: "Was soll Ihr Team prüfen? (optional)",
			doorNoteHint:
				"Nennen Sie die eine Sache, die Ihr Team ansehen soll, bevor es jemanden hineinlässt — z. B. „ein gültiger Presseausweis“. Genau das liest Ihr Team auf dem Bildschirm, während das Ticket wartet.",
			descriptionLabel: "Sollten Käufer etwas wissen? (optional)",
			descriptionHint:
				"Steht im Shop unter dem Namen der Ticketart und vollständig, sobald ein Käufer darauf tippt — leer lassen ändert nichts.",
			entryWindowLabel: "Wann gilt dieses Ticket?",
			entryWholeEvent: "Am ganzen Event",
			entryCertainDays: "An bestimmten Tagen",
			entryWholeEventHint:
				"Einlass an jedem Tag des Events — ändern Sie die Termine des Events, folgt der Einlass automatisch.",
			entryDaysGroupAriaLabel: "Tage, an denen dieses Ticket gilt",
			entryDayAriaLabel: "Einlass am {{day}}",
			entryPickFirstDay: "Ersten Tag wählen",
			entryPickLastDay:
				"Letzten Tag wählen — oder denselben Tag noch einmal antippen für nur einen Tag",
			entryFromLabelOnDay: "Einlass ab · {{day}}",
			entryToLabelOnDay: "Einlass bis · {{day}}",
			entryFromLabel: "Einlass ab",
			entryToLabel: "Einlass bis",
			entrySummaryOneDay: "Einlass am {{day}}, {{from}}–{{to}} ({{tz}}).",
			entrySummarySpan:
				"Einlass von {{start}}, {{from}} bis {{end}}, {{to}} ({{tz}}).",
			entrySummaryOneDayNoZone: "Einlass am {{day}}, {{from}}–{{to}}.",
			entrySummarySpanNoZone:
				"Einlass von {{start}}, {{from}} bis {{end}}, {{to}}.",
			salesAfterEntryWarning:
				"Hinweis: Der Verkauf läuft noch weiter, nachdem der Einlass geendet hat — in dieser Zeit wird ein Ticket für einen Tag verkauft, der schon vorbei ist.",
			salesWindowLabel: "Wann kann diese Ticketart gekauft werden?",
			salesFollowEvent: "Verkauf folgt dem Event",
			ownSalesWindow: "Diese Ticketart hat ein eigenes Verkaufsfenster",
			salesFollowEventHintDraft:
				"Der Verkauf folgt den Terminen des Events — hier ist nichts zu pflegen.",
			fallbackWindowLabel: "Sie folgt dem eigenen Verkaufsfenster des Events.",
			inheritedWindowLabel: "im Verkauf ab jetzt bis Event-Ende, {{date}}",
			inheritedWindowUntilEntryEnd:
				"im Verkauf ab jetzt bis zum Einlassende, {{date}}",
			salesFollowEventSuffix:
				"— ändern Sie die Termine des Events, folgt sie automatisch. Hier ist nichts zu pflegen.",
			salesWindowPlaceholder: "Wählen Sie Verkaufsstart und -ende",
			ownWindowHint:
				"Diese Termine gehören nur dieser Ticketart — die Termine des Events gelten für sie nicht mehr.",
			releaseLabel: "Wird sie sofort angezeigt?",
			releaseAlways: "Sofort verfügbar",
			releaseAfterSoldOut: "Erst wenn eine andere Ticketart ausverkauft ist",
			noChainCandidates:
				"Legen Sie zuerst eine weitere Ticketart an — es gibt noch nichts, wonach diese erscheinen könnte.",
			chainPrompt: "Erscheint, wenn diese Ticketart ausverkauft ist:",
			priceDescendsWarning:
				"Achtung: Diese Ticketart kostet weniger als die, nach der sie erscheint — das könnte Käuferinnen und Käufern auffallen.",
			predecessorClosesAtWarning:
				"Hinweis: Die vorangehende Ticketart hat ein eigenes Verkaufsende — danach erscheint diese Ticketart auch ohne vollständigen Ausverkauf.",
			whereLabel: "Wo findet das statt?",
			venueOverrideLabel: "Veranstaltungsort für dieses Ticket",
			useEventVenue: "Veranstaltungsort des Events verwenden",
			venueEmptyHint: "Der Veranstaltungsort des Events wird verwendet.",
			templateOverrideLabel: "Aussehen dieses Tickets",
			useEventTemplate: "Vorlage des Events verwenden",
			duplicate: "Duplizieren",
			duplicateName: "{{name}} (Duplikat)",
			deleteConfirmTitle: "Diese Ticketart löschen?",
			saveFailed:
				"Die Ticketart konnte nicht gespeichert werden — bitte versuchen Sie es erneut.",
			deleteFailed:
				"Die Ticketart konnte nicht gelöscht werden — bitte versuchen Sie es erneut.",
			deleteRefusalTitle: "Diese Ticketart kann nicht gelöscht werden",
			deleteRefusalLead:
				"Sie wurde bereits verkauft — jede Bestellung, jede Rechnung und jedes Ticket dazu übernimmt Name und Bedingungen von hier:",
			deleteRefusalTicketsOne: "1 Ticket trägt diese Ticketart",
			deleteRefusalTicketsOther: "{{count}} Tickets tragen diese Ticketart",
			deleteRefusalTicketsRemedy:
				"Ein erstattetes Ticket zählt weiterhin mit — seine Bestellung nennt diese Ticketart noch lange, nachdem das Geld zurück ist.",
			deleteRefusalConsumedOne: "1 ihrer Plätze ist bereits vergeben",
			deleteRefusalConsumedOther:
				"{{count}} ihrer Plätze sind bereits vergeben",
			deleteRefusalConsumedRemedy:
				"Ein vergebener Platz kommt erst zurück, wenn die Bestellung, die ihn genommen hat, in der Bestellübersicht gelöscht wird.",
			deleteRefusalRemedy:
				"Beenden Sie stattdessen den Verkauf: Setzen Sie das Verkaufsende auf jetzt, dann kann sie niemand mehr kaufen, während alles bereits Verkaufte Name und Preis behält.",
			soldConfirmTitleOne: "1 Ticket dieser Art ist bereits verkauft",
			soldConfirmTitleOther:
				"{{count}} Tickets dieser Art sind bereits verkauft",
			soldConfirmPriceOne:
				"1 Ticket ist bereits zu {{oldPrice}} verkauft. Es behält diesen Preis; neue Käufer zahlen {{newPrice}}.",
			soldConfirmPriceOther:
				"{{count}} Tickets sind bereits zu {{oldPrice}} verkauft. Sie behalten diesen Preis; neue Käufer zahlen {{newPrice}}.",
			soldConfirmCapacityBelow:
				"Die neue Kapazität von {{capacity}} liegt unter den bereits verkauften {{sold}}. Niemand verliert ein Ticket — diese Ticketart wird schlicht nicht mehr verkauft.",
			soldConfirmCapacity:
				"Die Kapazität ändert sich von {{oldCapacity}} auf {{newCapacity}}; damit bleiben {{remaining}} weitere zu verkaufen.",
			soldConfirmName:
				"Rechnungen und Korrekturbelege verwenden den Namen dieser Ticketart in dem Moment, in dem sie erstellt werden — nicht zum Zeitpunkt der Bestellung. Wenn Sie sie von „{{oldName}}“ in „{{newName}}“ umbenennen, zeigt jede Rechnung und jeder Korrekturbeleg, der für bereits verkaufte Tickets noch erstellt wird, den neuen Namen; bereits versendete Dokumente behalten den alten.",
			validation: {
				nameRequired: "Geben Sie zuerst einen Namen ein.",
				vatRange: "Die USt. muss zwischen 0 und 100 liegen.",
				priceNegative: "Der Preis darf nicht negativ sein.",
				capacityInteger: "Die Kapazität muss eine ganze Zahl ≥ 0 sein.",
				predecessorRequired:
					"Wählen Sie, nach welcher Ticketart diese erscheint.",
				salesWindowRequired:
					"Wählen Sie Verkaufsstart und -ende, oder lassen Sie sie dem Event folgen.",
				salesWindowOrder: "Das Verkaufsfenster muss nach seinem Start enden.",
				entryDaysRequired:
					"Wählen Sie die Tage, an denen dieses Ticket gilt — oder stellen Sie auf „Am ganzen Event“.",
				entryWindowOrder: "Das Einlassfenster muss nach seinem Beginn enden.",
				invalidTime:
					"{{time}} gibt es an diesem Tag in der Zeitzone des Veranstaltungsorts ({{tz}}) nicht — die Uhren überspringen diese Zeit, wenn sie im Frühjahr vorgestellt werden. Wählen Sie eine andere Uhrzeit.",
			},
		},
		dayClone: {
			dayTriggerAriaLabel: "Ticketarten von {{day}} duplizieren",
			nothingToCopy:
				"An diesem Tag gibt es keine Ticketart für einen einzelnen Tag zum Kopieren — ein mehrtägiger Pass gilt ohnehin an jedem Tag, den er abdeckt.",
			triggerAriaLabel: "Tage duplizieren",
			title: "Tage duplizieren",
			sourceDayLabel: "Welcher Tag soll kopiert werden?",
			sourceDayEmptyHint:
				"Noch keine Ticketart ist auf einen einzelnen Tag begrenzt — es gibt nichts zu kopieren.",
			changeSourceDayAriaLabel: "Anderen Tag wählen",
			summaryLine:
				"{{count}} Ticketarten werden kopiert: {{names}}. Der Einlass verschiebt sich auf den jeweiligen Tag, {{hours}} ({{tz}}).",
			chainBreakWarningOne:
				"{{names}} zeigt sich sonst erst, wenn eine Ticketart außerhalb dieses Tages ausverkauft ist — die Kopie zeigt sich sofort.",
			chainBreakWarningOther:
				"{{names}} zeigen sich sonst erst, wenn eine Ticketart außerhalb dieses Tages ausverkauft ist — die Kopien zeigen sich sofort.",
			targetDaysLabel: "Auf welche Tage?",
			targetDaysRequired: "Tage auswählen",
			submitLabel: "{{count}} Ticketarten anlegen",
			cloneFailed:
				"Die Ticketarten konnten nicht kopiert werden — bitte versuchen Sie es erneut.",
		},
	},
	vouchers: {
		nav: "Codes",
		list: {
			title: "Codes",
			intro:
				"Ein Code zieht Geld vom Warenkorb ab, schaltet eine im Shop versteckte Ticketart frei — oder beides.",
			new: "Neuer Code",
			empty: "Für diese Veranstaltung gibt es noch keinen Code.",
			loading: "Codes werden geladen",
			loaded: "{{count}} Codes geladen",
			loadFailed:
				"Die Codes konnten nicht geladen werden — bitte versuchen Sie es erneut.",
			usedOf: "{{used}} von {{limit}} genutzt",
			percentAll: "{{value}} % auf alles",
			percentOn: "{{value}} % auf {{types}}",
			percentOnUnlocked: "{{value}} % darauf",
			percentOnly: "{{value}} % Rabatt",
			fixedAll: "{{value}} auf alles",
			fixedOn: "{{value}} auf {{types}}",
			fixedOnUnlocked: "{{value}} darauf",
			fixedOnly: "{{value}} Rabatt",
			unlocks: "Schaltet {{types}} frei",
			unlocksUnnamedOne: "Schaltet 1 versteckte Ticketart frei",
			unlocksUnnamedOther: "Schaltet {{count}} versteckte Ticketarten frei",
			validUntil: "Gültig bis {{when}}",
			expiredOn: "Abgelaufen am {{when}}",
			usedUp: "Aufgebraucht",
			copyAria: "Code {{code}} kopieren",
			copied: "Code {{code}} kopiert.",
			copyFailed: "Der Code {{code}} konnte nicht kopiert werden.",
		},
		form: {
			codeLabel: "Code",
			codeHint:
				"4–32 Zeichen, A–Z, 0–9 und Bindestrich. Der Code ist der Name — er lässt sich später nicht ändern.",
			codeInvalid: "4–32 Zeichen, nur A–Z, 0–9 und Bindestrich.",
			generate: "Generieren",
			generatedNote:
				"Einmal-Code generiert — Nutzungen auf 1 und pro Person auf 1 gesetzt. Beides können Sie unten ändern.",
			labelLabel: "Wofür ist dieser Code?",
			labelHint: "Nur für Sie — Käufer sehen diesen Text nie.",
			jobHeading: "Was der Code tut",
			jobNone:
				"Ein Code muss mindestens eines von beidem tun — Geld abziehen oder ein Ticket freischalten.",
			discountTitle: "Geld abziehen",
			unlockTitle: "Versteckte Tickets freischalten",
			percent: "Prozent",
			fixed: "Fester Betrag",
			percentLabel: "Prozent vom Ticketpreis",
			fixedLabel: "Betrag in Euro",
			amountEmptyHint: "Leer lassen, wenn der Code kein Geld abziehen soll.",
			typeScope: "Nur auf diese Ticket-Typen.",
			chainScope:
				"Weitere Ticketarten erscheinen erst, wenn diese ausverkauft sind — ein darauf beschränkter Code gilt dann nicht mehr.",
			chainAdd: "Auch hinzufügen",
			unlockHint: "Nichts ausgewählt heißt: der Code schaltet nichts frei.",
			unlockEmpty:
				"Es gibt noch keine gesperrte Ticketart. Setzen Sie die Sichtbarkeit einer Ticketart auf „Nur mit Code“, dann erscheint sie hier.",
			hundredHeadline: "100 % ist kein Rabatt.",
			hundredBody:
				"Ein kostenloses Ticket ist ein eigener Ticket-Typ zu 0 €, den dieser Code freischaltet — kein auf null rabattierter Preis. Auf der Rechnung sind das zwei verschiedene Lieferungen, und nur die eine geht durch. Legen Sie die Ticketart zu 0 € an, setzen Sie ihre Sichtbarkeit auf „Nur mit Code“ und wählen Sie sie unten unter „Versteckte Tickets freischalten“.",
			hundredAction: "Betrag löschen und freischalten",
			hundredDismiss: "Zurück zum Feld",
			validityHeading: "Gültigkeit",
			usageLimit: "Nutzungen insgesamt",
			perBuyerLimit: "Nutzungen pro Person",
			perBuyerHint: "Leer lassen für unbegrenzt.",
			validUntil: "Gültig bis",
			noExpiry:
				"Kein Ablaufdatum — der Code gilt, bis die Nutzungen aufgebraucht sind.",
			expiresAt:
				"Läuft ab am {{when}} ({{zone}}). Danach wird der Code abgelehnt.",
			expiryImpossible:
				"{{when}} gibt es in {{zone}} nicht — die Uhren springen darüber hinweg. Bitte wählen Sie eine andere Zeit.",
			redemptions: "Eingelöst",
			redemptionsLoading: "Einlösungen dieses Codes werden gelesen",
			redemptionsNone: "Noch nicht eingelöst.",
			redemptionUnknownBuyer: "Bestellung {{orderId}}",
			redemptionCount: "{{count}}×",
			usedOf: "{{used}} von {{limit}} genutzt",
			cancel: "Abbrechen",
			save: "Code anlegen",
			saveExisting: "Speichern",
			saveFailed:
				"Der Code konnte nicht gespeichert werden — bitte versuchen Sie es erneut.",
			deleteConfirmTitle: "Diesen Code löschen?",
			deleteConfirmMessage:
				"Wer ihn noch hat, wird ab dann abgelehnt. Bestellungen, die ihn bereits genutzt haben, behalten ihren Rabatt.",
			deleteFailed:
				"Der Code konnte nicht gelöscht werden — bitte versuchen Sie es erneut.",
		},
	},
	locations: {
		common: {
			withinPrefix: "innerhalb von {{name}}",
		},
		list: {
			countSingular: "Location",
			countPlural: "Locations",
			addAriaLabel: "Location hinzufügen",
			loadingAriaLabel: "Locations werden geladen",
			errorTitle: "Locations konnten nicht geladen werden",
			errorBody:
				"Prüfen Sie Ihre Verbindung — die Location-Liste wird automatisch neu geladen.",
			deniedTitle: "Sie haben keinen Zugriff auf die Location-Liste",
			deniedBody:
				"Bitten Sie einen Administrator, Ihnen eine Rolle mit Event-Verwaltungszugriff zuzuweisen.",
			emptyTitle: "Noch keine Locations",
			emptyBody:
				"Ein Event braucht einen Veranstaltungsort, bevor es angelegt werden kann — legen Sie die erste an.",
			addFirst: "Erste Location anlegen",
			detailsAriaLabel: "{{name}}, Location-Details öffnen",
			openInMapsAriaLabel: "{{name}} in Google Maps öffnen",
			usedByOne: "verwendet von {{count}} Event",
			usedByOther: "verwendet von {{count}} Events",
			unused: "unbenutzt",
		},
		form: {
			saveUnconfirmed:
				"Noch nicht bestätigt – der Ort kann noch erscheinen. Speichern Sie erneut, falls nicht.",
			nameLabel: "Name",
			addressLabel: "Adresse",
			addressHint:
				"Die vollständige Postadresse — sie steht in der Kalender-Einladung und auf dem Wallet-Pass",
			timezoneLabel: "Zeitzone",
			timezoneHint:
				"Jede Uhrzeit, die Sie für diesen Veranstaltungsort eingeben, gilt in dieser Zeitzone",
			websiteLabel: "Website oder Maps-Link",
			notesLabel: "Hinweise zum Eingang",
			notesHint: "Bühneneingang, klingeln…",
			withinLabel: "Innerhalb von (dies ist ein Raum in…)",
			withinPlaceholder: "Keine übergeordnete Location",
			noParentVenue: "Keine übergeordnete Location",
			usedByWarningOne:
				"Wird von {{count}} Event verwendet — entfernen Sie es zuerst.",
			usedByWarningOther:
				"Wird von {{count}} Events verwendet — entfernen Sie diese zuerst.",
			deleteConfirmTitle: "Diese Location löschen?",
			saveFailed:
				"Die Location konnte nicht gespeichert werden — bitte versuchen Sie es erneut.",
			deleteFailed:
				"Die Location konnte nicht gelöscht werden — bitte versuchen Sie es erneut.",
			deleteRefusalTitle: "Diese Location kann nicht gelöscht werden",
			deleteRefusalLead:
				"Sie wird noch verwendet — Ticket-PDFs, Kalender-Einladungen und Wallet-Pässe übernehmen ihre Adresse beim Erstellen von hier:",
			deleteRefusalEventsOne: "1 Event findet hier statt",
			deleteRefusalEventsOther: "{{count}} Events finden hier statt",
			deleteRefusalEventsRemedy:
				"Ein Event weisen Sie über dessen Karte „Termin & Veranstaltungsort“ um.",
			deleteRefusalTicketTypesOne: "1 Ticketart schickt Gäste in diesen Raum",
			deleteRefusalTicketTypesOther:
				"{{count}} Ticketarten schicken Gäste in diesen Raum",
			deleteRefusalTicketTypesRemedy:
				"Ändern Sie den Raum bei der Ticketart, die ihn verwendet.",
			deleteRefusalChildLocationsOne: "1 Raum liegt darin",
			deleteRefusalChildLocationsOther: "{{count}} Räume liegen darin",
			deleteRefusalChildLocationsRemedy:
				"Einen Raum nehmen Sie über sein eigenes Feld „Innerhalb von“ heraus.",
			deleteRefusalRemedy:
				"Verschieben Sie jedes davon zuerst woandershin — sobald nichts mehr diese Location verwendet, lässt sie sich löschen.",
			overlayEdit: "Location bearbeiten",
			overlayNew: "Neue Location",
		},
		selectLocation: {
			defaultPlaceholder: "Veranstaltungsort wählen",
			noLocationsYet: "Noch keine Locations.",
			noSpecificVenue: "Kein bestimmter Veranstaltungsort",
		},
	},
	members: {
		common: {
			nameLabel: "Name",
			roleLabel: "Rolle",
			eventAccessLabel: "Event-Zugriff",
			permissionsLabel: "Berechtigungen",
			permissionsCountOne: "{{count}} Berechtigung",
			permissionsCountOther: "{{count}} Berechtigungen",
			builtInLabel: "Integriert",
		},
		header: {
			newRole: "Neue Rolle",
			invite: "Einladen",
		},
		list: {
			loadingAriaLabel: "Mitglieder werden geladen",
			errorTitle: "Mitglieder konnten nicht geladen werden",
			errorBody:
				"Prüfen Sie Ihre Verbindung — die Mitgliederliste wird automatisch neu geladen.",
			deniedTitle: "Sie haben keinen Zugriff auf die Mitgliederliste",
			deniedBody:
				"Bitten Sie einen Administrator, Ihnen eine Rolle mit Zugriff auf die Mitgliederliste zuzuweisen.",
			emptyTitle: "Noch keine Mitglieder",
			emptyBodyCanInvite:
				"Laden Sie Ihr erstes Teammitglied ein, um zu beginnen.",
			emptyBodyCannotInvite:
				"Mitglieder werden von Ihrem Betreiber hinzugefügt — hier ist noch niemand.",
			inviteSomeone: "Jemanden einladen",
			memberSingular: "Mitglied",
			memberPlural: "Mitglieder",
			pendingLabel: "ausstehend",
			pendingBadge: "Ausstehend",
			roleGroupAriaLabelOne:
				"Rolle {{role}}, {{count}} Mitglied, Rollendetails öffnen",
			roleGroupAriaLabelOther:
				"Rolle {{role}}, {{count}} Mitglieder, Rollendetails öffnen",
			pendingInviteAriaLabel: "Ausstehende Einladung für {{name}}",
		},
		detail: {
			closeAriaLabel: "Mitgliederdetails schließen",
			scopeLabel: "Event-Zugriff",
			grantedLabel: "Gewährt",
			grantedByOn: "Gewährt von {{name}} am {{date}}",
			initialProvisioning: "der Ersteinrichtung",
			formerTeamMember: "einem ehemaligen Teammitglied",
			removeButton: "Mitglied entfernen",
			changeRoleConfirmTitle: "Rolle zu „{{roleName}}“ ändern?",
			changeRoleConfirmMessage:
				"Die Berechtigungen werden sofort aktualisiert.",
			changeRoleConfirmText: "Rolle ändern",
			accessChangedRoleFailed:
				"Ihr Zugriff hat sich geändert — diese Rolle kann von hier aus nicht mehr bearbeitet werden.",
			roleUpdateFailed:
				"Die Rolle konnte nicht aktualisiert werden — bitte versuchen Sie es erneut.",
			removeConfirmTitle: "Dieses Mitglied entfernen?",
			removeConfirmMessage:
				"Der Zugriff auf diesen Workspace wird sofort entzogen.",
			removeConfirmText: "Entfernen",
			accessChangedRemoveFailed:
				"Ihr Zugriff hat sich geändert — dieses Mitglied kann von hier aus nicht mehr entfernt werden.",
			removeFailed:
				"Das Mitglied konnte nicht entfernt werden — bitte versuchen Sie es erneut.",
			roleLockSelf: "Sie können Ihre eigene Rolle nicht ändern.",
			soleOwnerLockReason:
				"Eine Organisation muss immer mindestens einen Owner haben.",
			removeLockSelf:
				"Sie können sich nicht selbst aus dem Workspace entfernen.",
			scopeAllEvents: "Alle Events",
			scopeEventsOne: "{{count}} Event",
			scopeEventsOther: "{{count}} Events",
		},
		invite: {
			closeAriaLabel: "Einladungsformular schließen",
			emailLabel: "E-Mail",
			emailPlaceholder: "name@unternehmen.de",
			allEventsChip: "Alle Events",
			specificEventsChip: "Bestimmte Events",
			selectAtLeastOne: "Wählen Sie mindestens ein Event aus.",
			coversAllEvents: "Gilt für alle aktuellen und zukünftigen Events.",
			send: "Einladung senden",
			sending: "Wird gesendet…",
			sentToast: "Einladung an {{email}} gesendet",
			sendFailed:
				"Die Einladung konnte nicht gesendet werden — bitte versuchen Sie es erneut.",
		},
		inviteDetail: {
			closeAriaLabel: "Einladungsdetails schließen",
			pendingLabel: "Ausstehende Einladung",
			sentOn: "Gesendet am {{date}}",
			allEventsCurrentFuture: "Alle Events · aktuell und zukünftig",
			resendButton: "Einladung erneut senden",
			resendButtonCooldown: "Einladung erneut senden ({{seconds}}s)",
			resendingLabel: "Wird gesendet…",
			revokeButton: "Einladung stornieren",
			revokingLabel: "Wird storniert…",
			revokeConfirmTitle: "Diese Einladung stornieren?",
			revokeConfirmMessage:
				"Dies storniert die Einladung — der Link funktioniert danach nicht mehr.",
			revokeConfirmText: "Einladung stornieren",
			revokeConfirmCancelText: "Behalten",
			revokeHint:
				"Entfernt diese ausstehende Einladung — der Link funktioniert danach nicht mehr.",
			resendFailed:
				"Die Einladung konnte nicht erneut gesendet werden — bitte versuchen Sie es erneut.",
			resentToast: "Einladung erneut gesendet",
			revokeFailed:
				"Die Einladung konnte nicht storniert werden — bitte versuchen Sie es erneut.",
			editFailed:
				"Die Einladung konnte nicht aktualisiert werden — bitte versuchen Sie es erneut.",
			editedToast: "Einladung aktualisiert",
			expired: "Abgelaufen",
			expiresInHoursOne: "Läuft in {{count}} Stunde ab",
			expiresInHoursOther: "Läuft in {{count}} Stunden ab",
			expiresTomorrow: "Läuft morgen ab",
			expiresInDaysOne: "Läuft in {{count}} Tag ab",
			expiresInDaysOther: "Läuft in {{count}} Tagen ab",
		},
		role: {
			namePlaceholder: "Rollenname",
			builtInNotice: "Integriert — kann nicht bearbeitet werden",
			editLabel: "Bearbeiten",
			toggleEditAriaLabel: "Diese Rolle bearbeiten",
			closeAriaLabel: "Rollendetails schließen",
			grantedCount: "{{count}} gewährt",
			affectedOne: "1 Mitglied betroffen",
			affectedZero: "Noch keine Mitglieder",
			affectedOther: "{{count}} Mitglieder betroffen",
			deleteButton: "Rolle löschen",
			deleteConfirmTitle: "Diese Rolle löschen?",
			deleteConfirmMessage:
				"Kein Mitglied nutzt diese Rolle. Diese Aktion kann nicht rückgängig gemacht werden.",
			deleteBlockedOne:
				"1 Mitglied nutzt diese Rolle — weisen Sie ihm zuerst eine andere zu.",
			deleteBlockedOther:
				"{{count}} Mitglieder nutzen diese Rolle — weisen Sie ihnen zuerst eine andere zu.",
			saveConfirmTitle: "Änderungen an dieser Rolle speichern?",
			saveConfirmMessageOne:
				"Dies ändert die Rolle für das 1 Mitglied, das sie trägt. Der Zugriff wird sofort aktualisiert.",
			saveConfirmMessageOther:
				"Dies ändert die Rolle für alle {{count}} Mitglieder, die sie tragen. Der Zugriff wird sofort aktualisiert.",
			createFailed:
				"Die Rolle konnte nicht erstellt werden — bitte versuchen Sie es erneut.",
			saveFailed:
				"Die Rolle konnte nicht gespeichert werden — bitte versuchen Sie es erneut.",
			deleteFailed:
				"Die Rolle konnte nicht gelöscht werden — bitte versuchen Sie es erneut.",
		},
		selectRole: {
			createNewRole: "Neue Rolle erstellen",
			viewGrantsAriaLabel: "Anzeigen, was {{role}} gewährt — {{permissions}}",
		},
		permissionPicker: {
			toggleDomain: "Alle Berechtigungen: {{domain}}",
			domainEvents: "Events",
			domainOrders: "Bestellungen",
			domainGuests: "Freikarten",
			domainCheckin: "Check-in",
			domainReports: "Berichte",
			domainMembers: "Mitglieder",
			domainGroupAriaLabel: "Berechtigungen für {{domain}}",
			allGranted: "alle gewährt",
			someGranted: "teilweise gewährt",
			noneGranted: "keine gewährt",
		},
		overlay: {
			memberAriaLabel: "Mitglied — {{name}}",
			inviteAriaLabel: "Ein Mitglied einladen",
			inviteDetailAriaLabel: "Ausstehende Einladung — {{name}}",
			roleAriaLabel: "Rolle — {{name}}",
		},
	},
	ticketTemplates: {
		variant: {
			editorial: "Editorial",
			stencil: "Stencil",
			strata: "Strata",
		},
		walletPreview: {
			appleTriggerLabel: "Apple Wallet Pass ansehen — {{variant}}",
			googleTriggerLabel: "Google Wallet Pass ansehen — {{variant}}",
			appleOverlayAriaLabel: "Vorschau des Apple Wallet Passes — {{variant}}",
			googleOverlayAriaLabel: "Vorschau des Google Wallet Passes — {{variant}}",
			appleNote:
				"Jede Zeile auf dem Pass ist eine Zeile der Ticketkarte, in deren eigenen Worten: dieselben Beschriftungen Datum, Ort, Ticket und Ticketnr. und dieselbe Datumszeile, einmal in der Zeitzone des Veranstaltungsortes geschrieben und unverändert angezeigt. Der Pass zeigt Ihr Logo ganz oben — die einzige Stelle, an der Apple überhaupt ein Logo zeichnet — und sonst nichts daneben: Das Logo ist die Marke, ein Name daneben würde dasselbe zweimal sagen, und das „Produced by“ der Ticketkarte bleibt am Fuß der Karte, wo es hingehört. Welche Datei verwendet wird, richtet sich nach dem Grund: auf den dunklen Looks die Wallet-Pass-Fassung, die die Organisationseinstellungen aus Ihrem Logo erzeugen, auf dem hellen Ihre eigene Datei, denn die Wallet-Pass-Fassung ist hell und wäre dort nicht zu sehen. Ist keine von beiden vorhanden, zeigt der Pass an dieser Stelle Ihren Namen. Wo der Eventname steht, hängt vom Look ab: Schreibt der Streifen ihn schon als Schriftzug, wiederholt ihn keine Textzeile; sonst zeichnet Apple den Namen über den Streifen, ohne Beschriftung, denn der Schriftzug der Karte trägt auch keine. Aus der Vorlage stammen: Hintergrund-, Text- und Beschriftungsfarbe, das Streifenbild, das Symbolbild des Passes — eine einfarbige Fläche in der Hintergrundfarbe des Passes, nie Ihr Logo — sowie jede Beschriftung und jeder Wert in der Sprache des Käufers. Alles Übrige zeichnet Apple — die Anordnung der Zeilen, die Beschriftungen in Großbuchstaben, die Schriftgrößen, die kleinere Schrift der Ticketangaben unter Datum und Ort sowie die weiße Fläche hinter dem Barcode. Eine Rückseite gibt es nicht: Es wird nichts gesendet, was die Karte nicht zeigt — die Postanschrift des Veranstaltungsortes verlässt das System also nie.",
			googleNote:
				"Jede Zeile auf dem Pass ist eine Zeile der Ticketkarte, in deren eigenen Worten: dieselben Beschriftungen Datum, Ort, Ticket und Ticketnr. und dieselbe Datumszeile, einmal in der Zeitzone des Veranstaltungsortes geschrieben. Die beiden Zeilen sind festgelegt — Google bekommt gesagt, welche Zelle genau wohin gehört — mit einem Unterschied, den wir nicht beseitigen können: Google hängt die rechte Zelle jeder Zeile nach rechts, während die Karte beide nach links hängt. Googles eigenes Veranstaltungsort-Feld wird gar nicht verwendet, denn es kann keinen Ortsnamen tragen, ohne zugleich eine Postanschrift zu tragen — und die Karte zeigt keine Adresse. Die Marke steht oben als Aussteller, wo Google sie hinsetzt; die Ticketkarte trägt sie am Fuß. Aus der Vorlage stammen außerdem: die Hintergrundfarbe des Passes, das Logo des Ausstellers und der Barcode-Wert. Welche Datei das ist, richtet sich nach dem Grund, genauso wie es ein Apple-Pass wählt: auf den dunklen Looks die Wallet-Pass-Fassung, die die Organisationseinstellungen aus Ihrem Logo erzeugen, auf dem hellen Ihre eigene Datei, denn die Wallet-Pass-Fassung ist hell und wäre dort nicht zu sehen; Google lädt genau diese Datei — die hier gezeigte. Gibt es für einen dunklen Look noch keine Wallet-Pass-Fassung, zeigt diese Vorschau das Logo selbst, das Google nur als PNG oder JPEG annimmt — eine WebP-Datei oder gar keine Datei bleibt weg, und Google zeichnet seinen eigenen allgemeinen Platzhalter. Welche Datei Google auch bekommt, es zeichnet sie zweimal: als den breiten Kartentitel, für den diese Vorschau steht, und noch einmal in einem zweiten Feld, das quadratisch verlangt und rund beschnitten wird — ein breiter Schriftzug wird dort auf eine Scheibe zugeschnitten, und diesen Beschnitt kann hier nichts zeigen. Ein Bandbild wird nicht mitgeschickt: Google setzt eines unter die Zeilen des Passes, und die Ticketkarte hat keinen solchen Abschnitt. Die Schriftgrößen, die Textfarbe (die Google passend zum Hintergrund wählt, statt sie von uns zu lesen) und die Barcode-Fläche gehören weiterhin Google.",
		},
	},
	sale: {
		common: {
			eventPickerLabel: "Event",
		},
		guestIssue: {
			title: "Gästetickets ausstellen",
			subtitle:
				"Freikarten für Partner und Presse — sie zählen zur Kapazität, genau wie ein Verkauf.",
			loadingAriaLabel: "Gästetickets werden geladen",
			noEventsTitle: "Noch keine Events",
			noEventsBody:
				"Legen Sie zuerst ein Event an — Gästetickets brauchen eines, gegen das sie ausgestellt werden.",
			noTypesTitle: "Noch keine Ticketarten",
			noTypesBody:
				"Dieses Event hat noch keine Ticketarten — legen Sie eine an, bevor Sie ein Gästeticket ausstellen.",
			nameLabel: "Name der Empfängerin/des Empfängers",
			emailLabel: "E-Mail der Empfängerin/des Empfängers",
			emailPlaceholder: "name@galerie.de",
			localeLabel: "Ticketsprache",
			typeLabel: "Ticketart",
			qtyLabel: "Anzahl",
			addRowButton: "Weitere Ticketart hinzufügen",
			removeRowAriaLabel: "Diese Ticketart entfernen",
			submitButton: "Tickets ausstellen",
			submitting: "Wird ausgestellt…",
			pendingLabel: "Tickets werden an {{name}} ausgestellt…",
			settledTitle: "Tickets ausgestellt",
			settledBody:
				"An {{name}} ({{email}}) ausgestellt — die Ticket-E-Mail ist unterwegs.",
			issueAnotherButton: "Weitere ausstellen",
			refusedTitle: "Diese Tickets konnten nicht ausgestellt werden",
			refusedBodySoldOut:
				"Eine der ausgewählten Ticketarten ist inzwischen ausverkauft — wählen Sie eine andere Art oder eine kleinere Anzahl.",
			refusedBodyGeneric: "Etwas ist schiefgelaufen — versuchen Sie es erneut.",
			tryAgainButton: "Erneut versuchen",
			writeFailed:
				"Die Tickets konnten nicht ausgestellt werden — versuchen Sie es erneut.",
			issuedCountLabel: "{{count}} ausgestellt",
			searchLabel: "Nach Name, E-Mail oder Ticketnummer suchen",
			searchAriaLabel:
				"Gästebestellungen nach Name, E-Mail oder Ticketnummer suchen",
			searchBuyerLabel: "Nach Name oder E-Mail suchen",
			searchBuyerAriaLabel:
				"Gästebestellungen nach Name oder E-Mail durchsuchen",
			addGuestButton: "Gast hinzufügen",
			noAccessBody:
				"Bitten Sie einen Admin um eine Rolle mit Zugriff auf Verkaufsberichte.",
			noGuestOrdersTitle: "Noch keine Gästetickets ausgestellt",
			noGuestOrdersBody:
				"Fügen Sie einen Gast hinzu, um die erste Freikarte für dieses Event auszustellen.",
			noMatchTitle: "Keine Treffer",
			noMatchBody: "Keine Gästebestellung entspricht dieser Suche.",
			mfaRequiredBody:
				"Für dieses Konto muss die Anmeldung in zwei Schritten eingeschaltet sein, bevor es Gästetickets ausstellen oder sehen kann.",
			addDialogAriaLabel: "Einen Gast hinzufügen",
			nameRequiredError: "Geben Sie den Namen des Gastes ein",
			emailRequiredError: "Geben Sie eine E-Mail-Adresse ein",
			emailInvalidError: "Geben Sie eine gültige E-Mail-Adresse ein",
			freeLabel: "Kostenlos",
			capReached: "Mehr als {{cap}} gehen nicht auf einmal.",
			typeRowDescriptionAriaLabel: "Weitere Informationen zu {{name}}",
			typeRowNameAriaLabel: "{{name}} — vollständig anzeigen",
			typeRowDescriptionBackLabel: "Zurück",
			incrementAriaLabel: "Eine weitere {{name}}",
			decrementAriaLabel: "Eine weniger {{name}}",
			qtyFieldAriaLabel: "Anzahl {{name}}",
		},
	},
	checkin: {
		list: {
			emptyBody:
				"Bitten Sie eine Veranstaltungsleitung, eines anzulegen, bevor Sie jemanden einlassen können.",
		},
	},
	settings: {
		title: "Einstellungen",
		nav: {
			organization: "Organisation",
			integrations: "Integrationen",
			legal: "Rechtliche Dokumente",
		},
		organization: {
			sectionSite: "Website",
			siteOrigin: "Öffentliche Website-URL",
			mapsApiKey: "Google-Maps-Browserschlüssel",
			seoTitle: "Titel in Suchergebnissen",
			seoDescription: "Beschreibung in Suchergebnissen",
			seoLocale: "Sprache der Website",
			socialImage: "Vorschaubild: URL oder Storage-Pfad",
			favicon: "Browser-Symbol: URL oder Storage-Pfad",
			appleTouchIcon: "Startbildschirm-Symbol: URL oder Storage-Pfad",
			siteInvalid:
				"Bitte Website-URL und Bildpfade prüfen. Die Website benötigt eine HTTPS-URL ohne Pfad.",
			title: "Organisation",
			subtitle:
				"Ihre Marke und rechtliche Identität — wie sie im Shop und auf jedem Beleg erscheint.",
			sectionBrand: "Marke",
			brandName: "Markenname",
			brandOrg: "Organisation",
			brandOrgHint:
				"Die betreibende Organisation, falls abweichend vom Markennamen.",
			brandSeed: "Markenfarbe",
			brandSeedHint:
				"Die Hauptfarbe — aus ihr entsteht das ganze Farbschema des Shops.",
			logo: "Logo",
			logoHint:
				"Ein einfarbiges Logo auf durchsichtigem Hintergrund, im Seitenverhältnis etwa 1500×326. Nur die Form wird verwendet — ein farbiges Bild oder ein deckender Hintergrund erscheint als flache Silhouette.",
			logoPreviewLightAriaLabel: "Logo-Vorschau auf hellem Hintergrund",
			logoPreviewDarkAriaLabel: "Logo-Vorschau auf dunklem Hintergrund",
			logoChooseButton: "Logo hochladen",
			logoChangeButton: "Logo ändern",
			logoRemoveButton: "Logo entfernen",
			logoTooLargeError:
				"Diese Bilddatei ist größer als 2 MB — bitte eine kleinere Datei wählen.",
			logoUnsupportedTypeError:
				"Nur JPG-, PNG- oder WEBP-Bilder werden unterstützt.",
			walletMarkVerdictDerive:
				"Dieses Logo ist dunkel, und auf den dunklen Looks zeichnen Wallet-Pässe es auf dunklem Grund. Für diese Looks wird eine helle Fassung erzeugt — Ihre eigene Datei bleibt unverändert gespeichert.",
			walletMarkVerdictColor:
				"Dieses Logo ist dunkel und farbig. Auf den dunklen Looks zeichnen Wallet-Pässe es auf dunklem Grund, wo es kaum zu sehen sein wird — eine helle Fassung würde jedoch die Farbe verlieren.",
			walletMarkVerdictTwoTone:
				"Dieses Logo ist dunkel mit hell ausgesparten Teilen. Auf den dunklen Looks zeichnen Wallet-Pässe es auf dunklem Grund, wo es kaum zu sehen sein wird — eine helle Fassung würde jedoch die Aussparungen zulaufen lassen.",
			walletMarkVerdictKeep:
				"Dieses Logo ist auf den dunklen Looks gut zu lesen, wo ein Wallet-Pass es auf dunklem Grund zeichnet. Der helle Look zeichnet seinen Pass auf hellem Grund und verwendet dieselbe Datei unverändert, wo sie möglicherweise nicht zu lesen ist.",
			walletMarkVerdictUnreadable:
				"Diese Datei ließ sich nicht als Bild lesen — bitte eine andere wählen.",
			walletMarkVerdictBlank:
				"Dieses Bild enthält nichts Sichtbares — bitte ein anderes wählen.",
			walletMarkVerdictUnmeasurable:
				"Dieser Browser konnte das Bild nicht zur Prüfung lesen. Bitte einen anderen Browser oder eine andere Datei versuchen.",
			walletMarkChoiceOriginal: "Meines behalten",
			walletMarkChoiceLight: "Helle Fassung verwenden",
			walletMarkMissingNote:
				"Für dieses Logo gibt es noch keine Wallet-Pass-Fassung, deshalb greift ein Wallet-Pass auf den dunklen Looks auf Ihre eigene Datei zurück, die dort kaum zu lesen ist — und ist diese Datei eine WebP-Datei, nehmen weder Apple noch Google sie an und der Pass zeigt gar kein Logo.",
			walletMarkGenerateButton: "Wallet-Pass-Fassung erzeugen",
			walletMarkArmedNote:
				"Die Wallet-Pass-Fassung wird beim Speichern aus diesem Logo erzeugt.",
			walletMarkGenerateFailed:
				"Die Änderungen wurden gespeichert, aber die Wallet-Pass-Fassung ließ sich aus diesem Logo nicht erzeugen. Bitte erneut versuchen oder das Logo neu hochladen.",
			sectionLegal: "Rechtliche Angaben",
			legalName: "Firmenname",
			address: "Adresse",
			vatId: "USt-IdNr.",
			register: "Register",
			representative: "Vertreten durch",
			contactEmail: "Kontakt-E-Mail",
			contactEmailHint:
				"Die Adresse, unter der Käufer und Behörden einen Menschen erreichen (§ 5 DDG) — anders als die Absender-E-Mail unten, von der automatisierte E-Mails kommen.",
			phone: "Telefon",
			phoneHint: "Optional — ein zweiter schneller Kontaktweg (§ 5 DDG).",
			sectionEmail: "E-Mail",
			emailSender: "Absender-E-Mail",
			emailSenderHint:
				"Die Absenderadresse, von der Ticket- und Beleg-E-Mails versendet werden.",
			emailSenderPlatformHint:
				"Die Adresse der Plattform. Verbinden Sie unter Integrationen Ihr eigenes Lettermint-Konto, um von Ihrer eigenen zu senden.",
			requiredFieldMessage: "Dieses Feld ist erforderlich.",
			emailValidationMessage: "Bitte eine gültige E-Mail-Adresse eingeben.",
			licence: {
				active: "Lizenziert bis {{until}}.",
				grace:
					"Lizenz am {{ended}} abgelaufen. Der Ticketverkauf endet am {{stops}}.",
				readOnly:
					"Lizenz am {{ended}} abgelaufen. Ticketverkauf seit {{stopped}} pausiert.",
				missing:
					"Keine gültige Lizenz hinterlegt. Der Ticketverkauf ist pausiert.",
			},
			publicNote:
				"Diese Angaben sind öffentlich — jeder Käufer sieht sie im Shop und auf jeder Rechnung.",
			readOnlyNote:
				"Ihre Rolle umfasst nicht die Verwaltung der Organisationsidentität. Bitten Sie einen Owner oder Admin um Zugriff.",
			saveFailed:
				"Die Änderungen konnten nicht gespeichert werden — bitte erneut versuchen.",
			saveSuccessToast: "Organisationsidentität aktualisiert.",
		},
		legal: {
			title: "Rechtliche Dokumente",
			subtitle:
				"Ihre eigenen Nutzungsbedingungen und Datenschutzerklärung — bis sie veröffentlicht sind, gilt der Standardtext der Plattform.",
			tabTerms: "Nutzungsbedingungen",
			tabPrivacy: "Datenschutzerklärung",
			charCount: "{{count}} / {{max}} Zeichen",
			previewLabel: "Vorschau",
			publishButton: "Veröffentlichen",
			publishSuccessToast: "Veröffentlicht.",
			publishFailed:
				"Veröffentlichung fehlgeschlagen — bitte erneut versuchen.",
			publishNoAnswer:
				"Noch keine Antwort – möglicherweise wurde trotzdem veröffentlicht. Bitte prüfen Sie das, bevor Sie erneut veröffentlichen.",
			formatFailed: "Formatierung fehlgeschlagen — bitte erneut versuchen.",
			formatNoAnswer:
				"Die Formatierung hat nicht rechtzeitig geantwortet — bitte erneut versuchen.",
			readOnlyNote:
				"Ihre Rolle umfasst nicht die Veröffentlichung rechtlicher Dokumente. Bitten Sie einen Owner oder Admin um Zugriff.",
			pasteHint:
				"In beliebiger Sprache, so wie Sie sie haben. Wir erkennen die Sprache und bereiten Ihnen einen deutschen und einen englischen Entwurf zur Prüfung vor.",
			sourceTextLabel: "Ausgangstext",
			formatAndTranslateButton: "Formatieren & übersetzen",
			writeManuallyButton: "Stattdessen beide Sprachen selbst schreiben",
			formattingNote:
				"Ihr Dokument wird formatiert und übersetzt — das kann bis zu einer Minute dauern, kein Neuladen nötig.",
			localeLabelDe: "Deutsch",
			localeLabelEn: "Englisch",
			provenancePastedByYou: "Von Ihnen eingefügt",
			provenanceTranslatedByAi: "Von der KI übersetzt",
			editSourceLabel: "Ausgangstext bearbeiten",
			previewAriaLabel: "Vorschau ({{locale}})",
			editAriaLabel: "Ausgangstext bearbeiten ({{locale}})",
			platformSectionNote:
				"Jede Datenschutzerklärung enthält außerdem einen von der Plattform gepflegten Abschnitt „Empfänger & Auftragsverarbeiter”. Sie können ihn nicht bearbeiten oder entfernen — er aktualisiert sich von selbst, sobald dieser Shop einen neuen Dienst anbindet, und erscheint bereits am Ende beider Dokumente unten.",
			platformSectionDiscloses: "Aktuell aufgeführt: {{vendors}}.",
			advisoryReadyHeading: "Bereit zur Veröffentlichung",
			advisoryReady:
				"Beim Veröffentlichen ersetzt Ihr Text den Standardtext der Plattform in beiden Sprachen.",
			advisoryHeadingLocales: "Fehlender Text",
			advisoryHeadingVersion: "Automatische Version",
			advisoryLocalesBothEmpty:
				"Der deutsche und der englische Text sind beide leer. Wenn Sie jetzt veröffentlichen, sieht kein Leser in einer der beiden Sprachen echten Text — die Seite bleibt leer, bis Sie Text hinzufügen.",
			advisoryLocaleDeEmpty:
				"Der deutsche Text ist leer. Wenn Sie jetzt veröffentlichen, sehen deutsche Leser keinen echten Text — nur englische Leser bekommen das Dokument zu sehen, das Sie geschrieben haben.",
			advisoryLocaleEnEmpty:
				"Der englische Text ist leer. Wenn Sie jetzt veröffentlichen, sehen englische Leser keinen echten Text — nur deutsche Leser bekommen das Dokument zu sehen, das Sie geschrieben haben.",
			advisoryVersionBlank:
				"Ihr Dokument nennt selbst keine Version, daher wird der Tag der Eingabe — {{date}} — als Version eingetragen. Sie können sie in jeder der beiden Sprachfassungen ändern, falls das Dokument eine eigene Version nennt.",
			reviewHeading: "Rechtliche Prüfung",
			reviewNote:
				"Eine KI-Prüfung Ihres eigenen Textes daraufhin, was das deutsche Recht verlangt. Sie ist keine Rechtsberatung und blockiert die Veröffentlichung nie.",
			reviewSummaryNever: "Noch nicht geprüft",
			reviewSummaryRunning: "Wird geprüft…",
			reviewSummaryUnavailable: "Prüfung nicht durchgelaufen",
			reviewSummaryClean: "Nichts zu beanstanden",
			reviewSummaryFindings: "{{count}} zu prüfen",
			reviewBodyNever:
				"Bisher wurde nichts geprüft. Starten Sie die Prüfung, wann Sie möchten — sie liest beide Sprachfassungen gemeinsam, und die Veröffentlichung wartet nie auf sie.",
			reviewBodyRunning:
				"Beide Sprachfassungen werden gerade daraufhin geprüft, was das deutsche Recht verlangt. Das dauert bis zu einer Minute — Sie können dieses Fenster schließen und weiterarbeiten.",
			reviewBodyUnavailable:
				"Die Prüfung selbst ist nicht durchgelaufen; das sagt nichts über Ihr Dokument aus. Die Veröffentlichung ist davon unberührt. Bitte gleich noch einmal versuchen.",
			reviewBodyClean:
				"Die Prüfung hat beide Sprachfassungen gelesen und nichts beanstandet.",
			reviewStaleNote:
				"Ihr Text hat sich seit dieser Prüfung geändert — alles Folgende bezieht sich auf die frühere Fassung.",
			reviewRunButton: "Prüfung starten",
			reviewRerunButton: "Erneut prüfen",
			reviewUnchangedToast:
				"Seit der letzten Prüfung hat sich nichts geändert — die Punkte gelten unverändert.",
			reviewSeverityCritical: "Kritisch",
			reviewSeverityWarning: "Warnung",
			reviewSeverityAdvisory: "Verbesserungswürdig",
			reviewSeverityVerify: "Von Ihnen zu bestätigen",
			reviewEvidenceLabel: "Textstelle",
			replaceAriaLabel: "{{kind}}-Entwurf durch neuen Einfüge-Text ersetzen",
			replaceConfirmTitle:
				"{{kind}}-Entwurf durch einen neuen Einfüge-Text ersetzen?",
			replaceConfirmMessage:
				"Dadurch werden der aktuelle deutsche und englische Text für dieses Dokument gelöscht. Das kann im Editor nicht rückgängig gemacht werden.",
			replaceConfirmAction: "Ersetzen",
		},
	},
	integrations: {
		titles: {
			lettermint: "E-Mail-Versand",
			revolutSandbox: "Zahlungen (Sandbox)",
			revolutLive: "Zahlungen (live)",
			appleWallet: "Apple-Wallet-Pässe",
			googleWallet: "Google-Wallet-Pässe",
			gemini: "KI-Generierung",
			metaAds: "Meta-Werbung (Instagram / Facebook)",
		},
		status: {
			notConfigured: "Nicht eingerichtet",
			verifying: "Wird geprüft…",
			verified: "Geprüft",
			checkFailed: "Prüfung fehlgeschlagen ({{reason}})",
			checkFailedDetail: "Prüfung fehlgeschlagen ({{reason}}) — {{detail}}",
			saveFailed: "Speichern fehlgeschlagen ({{reason}})",
		},
		panel: {
			configureAriaLabel: "{{name}} einrichten",
			manageAriaLabel: "{{name}} verwalten",
			readDenied: "Sie haben keinen Zugriff auf den Status der Integrationen.",
			readError: "Der Status der Integrationen konnte nicht geladen werden.",
			verify: "Prüfen",
			saveAndVerify: "Speichern & prüfen",
			secretKeepHint:
				"Leer lassen, um das bereits Gespeicherte zu behalten — ein gespeicherter Schlüssel wird nie wieder angezeigt, ein leeres Feld bedeutet also „behalten“, nicht „löschen“.",
			secretWriteOnlyHint: "Wird nach dem Speichern nie wieder angezeigt.",
		},
		lettermint: {
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			platformMail:
				"E-Mails gehen von {{sender}} über das E-Mail-Konto der Plattform raus, bis Sie Ihr eigenes Lettermint-Konto verbinden.",
			platformMailUntilSender:
				"Ihr Lettermint-Konto ist verbunden. E-Mails gehen weiter von {{sender}} über das Konto der Plattform raus, bis Sie unter Organisation Ihre eigene Absenderadresse eintragen.",
			wizardAriaLabel: "Lettermint-E-Mail-Zustellung einrichten",
			form: {
				closeAriaLabel: "Formular für Lettermint-Schlüssel schließen",
				intro:
					"Lettermint verwendet zwei verschiedene Schlüssel: Das Projekt-Token verschickt E-Mails, das Team-Token verwaltet Ihr Konto und die Zustellverfolgung. Das eine dort einzufügen, wo das andere hingehört, ist der naheliegendste Fehler — deshalb prüft jedes Feld schon beim Eintippen, ob das Token mit den richtigen Zeichen beginnt.",
				projectTokenLabel: "Projekt-API-Token",
				projectTokenHint:
					"Mit diesem Schlüssel verschickt der Shop E-Mails — Tickets, Belege, Anmeldecodes. Er lässt sich nicht automatisch prüfen und wird deshalb so gespeichert, wie Sie ihn einfügen. Wenn nach dem Speichern keine E-Mails mehr verschickt werden, prüfen Sie zuerst dieses Feld. Sie finden ihn in Ihrem Lettermint-Dashboard unter Project → API tokens.",
				projectTokenKeepHint:
					"Leer lassen, um das bereits gespeicherte Projekt-Token zu behalten — es wird nie wieder angezeigt, leer bedeutet also „behalten“, nicht „löschen“. Fügen Sie nur dann ein neues ein, wenn Sie es ersetzen möchten; wenn Sie nur das Team-Token ändern, bleibt dieses hier unverändert. Sie finden es in Ihrem Lettermint-Dashboard unter Project → API tokens.",
				projectTokenError:
					"Dieses Feld braucht das Token, das mit lm_ beginnt — nicht mit lm_team_.",
				projectTokenRevealAriaLabel: "Projekt-Token anzeigen",
				projectTokenHideAriaLabel: "Projekt-Token verbergen",
				teamTokenLabel: "Team-API-Token",
				teamTokenHint:
					"Dieser Schlüssel verwaltet Ihr Lettermint-Konto — Zustellrouten und Zustellverfolgung; E-Mails verschickt er nie. Er ist der, der tatsächlich geprüft wird: Ist er falsch oder fehlen ihm die Kontoverwaltungsrechte, schlägt das Speichern sofort fehl, bevor irgendetwas gespeichert wird. Sie finden ihn in Ihrem Lettermint-Dashboard unter Team settings → API tokens.",
				teamTokenKeepHint:
					"Leer lassen, um das bereits gespeicherte Team-Token zu behalten — es wird nie wieder angezeigt, leer bedeutet also „behalten“, nicht „löschen“. Das gespeicherte wird ohnehin bei jedem Speichern erneut geprüft. Fügen Sie nur dann ein neues ein, wenn Sie es ersetzen möchten. Sie finden es in Ihrem Lettermint-Dashboard unter Team settings → API tokens.",
				teamTokenError:
					"Das ist das Sende-Token — dieses Feld braucht das, das mit lm_team_ beginnt.",
				teamTokenRevealAriaLabel: "Team-Token anzeigen",
				teamTokenHideAriaLabel: "Team-Token verbergen",
				docsLink: "Wo Sie diese Tokens finden",
				saveButton: "Speichern & prüfen",
				saving: "Wird gespeichert…",
				overwriteConfirm: {
					projectToken: {
						title: "Ihr Projekt-Token ersetzen?",
						message:
							"Ausgehende E-Mails verwenden sofort das neue Projekt-Token. Das gespeicherte Team-Token bleibt unverändert.",
					},
					teamToken: {
						title: "Ihr Team-Token ersetzen?",
						message:
							"Zustellrouten und Zustellverfolgung verwenden sofort das neue Team-Token. Der E-Mail-Versand ist davon nicht betroffen — das gespeicherte Projekt-Token bleibt unverändert.",
					},
					both: {
						title: "Lettermint-Schlüssel ersetzen?",
						message:
							"Ausgehende E-Mails und die Zustellverfolgung verwenden die neuen Tokens sofort.",
					},
				},
				overwriteConfirmButton: "Ersetzen",
				verifyErrors: {
					invalid:
						"Lettermint hat dieses Token abgelehnt — prüfen Sie, ob Sie das Team-Token und nicht das Projekt-Token aus Settings → API keys kopiert haben.",
					notAuthorized:
						"Dieses Token darf die Zustellverfolgung nicht verwalten — verwenden Sie eines mit Kontoverwaltungsrechten.",
					unreachable:
						"Lettermint konnte zur Prüfung des Team-Tokens nicht erreicht werden — prüfen Sie Ihre Verbindung und speichern Sie erneut.",
					notConfigured:
						"An den Tokens lag es nicht — dieses Speichern wurde abgelehnt, weil an diesem Shop noch etwas nicht eingerichtet ist. Eine Administratorin ergänzt das in den Einstellungen, danach speichern Sie erneut.",
				},
				transportErrors: {
					unauthenticated:
						"Ihre Sitzung ist abgelaufen — melden Sie sich erneut an und speichern Sie noch einmal.",
					notAuthorized:
						"Sie dürfen die Integrationseinstellungen nicht verwalten — stellen Sie sicher, dass die Anmeldung in zwei Schritten eingeschaltet ist, oder bitten Sie einen Administrator um Zugriff.",
					invalidInput:
						"Dieses Speichern wurde abgelehnt, bevor etwas gespeichert wurde — laden Sie die Seite neu, fügen Sie dann beide Tokens ein und speichern Sie erneut.",
					unknown:
						"Beim Speichern der Schlüssel ist etwas schiefgelaufen — versuchen Sie es erneut.",
				},
				route: {
					label: "Zustellroute",
					hint: "Legt fest, welche Lettermint-Route die Zustellstatus-Updates dieses Workspaces überträgt.",
					notYetAvailable:
						"Speichern Sie zuerst Ihre Tokens — öffnen Sie dieses Fenster anschließend erneut, um die Route für Zustellstatus-Updates auszuwählen.",
					loadError:
						"Ihre Lettermint-Routen konnten nicht geladen werden — prüfen Sie Ihre Verbindung und versuchen Sie es erneut.",
					retryButton: "Erneut versuchen",
					empty:
						"In Ihrem Lettermint-Konto wurden noch keine Zustellrouten gefunden. Erstellen Sie dort eine und öffnen Sie dieses Fenster danach erneut.",
					defaultBadge: "Standard",
				},
			},
		},
		revolut: {
			confirmation: {
				doneButton: "Fertig",
				title: "Zur Sandbox wechseln",
				consequence: "Neue Bestellungen verwenden Testzahlungen.",
				done: "Sandbox ist aktiv",
				sendError:
					"Der Code konnte nicht gesendet werden. Bitte erneut versuchen.",
				codeError:
					"Dieser Code ist ungültig oder abgelaufen. Bitte erneut versuchen oder einen neuen Code anfordern.",
				saveError:
					"Der Wechsel konnte nicht abgeschlossen werden. Bitte erneut versuchen.",
				sendAgain: "Code senden",
			},
			overview: {
				editEnvironment: "{{env}} bearbeiten",
				title: "Revolut",
				unavailable: "Kontostatus nicht verfügbar",
				notChecked: "Nicht geprüft",
				notConnected: "Nicht verbunden",
				platformSandbox: "Sandbox · Plattformkonto",
				ownSandbox: "Sandbox · Ihr Konto",
				ownLive: "Live · Ihr Konto",
			},
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			platformSandbox: "Testkonto der Plattform (Standard)",
			wizardAriaLabel: "Revolut {{env}} einrichten",
			wizard: {
				envLive: "Live",
				envSandbox: "Sandbox",
				closeAriaLabel: "Einrichtung für Revolut {{env}} schließen",
				intro:
					"Aktivieren Sie Apple Pay, Google Pay, Kartenzahlung und Revolut Pay für den Ticketkauf.",
				prereqNote: "Sie benötigen ein Revolut-Business-Konto mit API-Zugriff.",
				docsLink: "API-Schlüssel bei Revolut holen",
				secretKeyLabel: "Secret Key",
				secretKeyHint:
					"Nach dem Speichern wird dieser Schlüssel nie wieder angezeigt oder für Sie vorausgefüllt. Sie brauchen ihn für die Einrichtung.",
				secretKeyKeepHint:
					"Leer lassen, um den bereits gespeicherten Schlüssel zu behalten — leer bedeutet „behalten“, nicht „löschen“. So können Sie Währung, Publishable Key oder die aktive Umgebung ändern, ohne einen Schlüssel neu einzutippen, der Ihnen nie angezeigt wird. Fügen Sie nur dann einen neuen Schlüssel ein, wenn Sie ihn ersetzen möchten.",
				secretKeyBadCharsError:
					"Keine Leerzeichen oder Zeilenumbrüche — als eine Zeile einfügen.",
				secretKeyMissingPrefixError:
					"Muss mit sk_ beginnen — so vergibt Revolut Secret Keys.",
				publicKeyLabel: "Publishable Key",
				publicKeyHint:
					"Gehört zum Secret Key oben — der Shop verwendet ihn erst, wenn Sie diese Umgebung unten auch einschalten.",
				publicKeyKeepHint:
					"Leer lassen, um den für diese Umgebung bereits gespeicherten Publishable Key zu behalten — leer bedeutet „behalten“, nicht „löschen“. Fügen Sie nur dann einen neuen Key ein, wenn Sie ihn ersetzen möchten.",
				publicKeyRequiredWhenActivateError:
					"Geben Sie auch den Publishable Key ein — zum Einschalten werden beide Schlüssel benötigt.",
				currencyLabel: "Checkout-Währung",
				currencyHint:
					"Ein 3-stelliger Währungscode wie EUR — gilt für Sandbox und Live gemeinsam.",
				currencyFormatError:
					"Geben Sie einen 3-stelligen Währungscode ein, z. B. EUR.",
				applePayDomainLabel: "Apple-Pay-Domain",
				applePayDomainHint:
					"Die Webadresse, die Apple Pay für den Checkout dieses Shops prüft — nur bei Live. Bereits mit der eigenen Adresse dieses Shops ausgefüllt; ändern Sie sie nur, wenn der Checkout woanders läuft.",
				applePayDomainFormatError:
					"Nur die Adresse, z. B. tickets.example.com — kein https:// und keine Leerzeichen.",
				activeEnvNote:
					"Das Speichern prüft und speichert diesen Schlüssel — es ändert nicht, welche Umgebung der Checkout gerade verwendet.",
				activateLabel: "{{env}} für den Checkout verwenden",
				activateHint:
					"Der Checkout kann nur eine von beiden gleichzeitig nutzen — wenn Sie dies einschalten, wechseln neue Bestellungen zu {{env}}, sobald dieser Schlüssel geprüft ist.",
				activateConfirmTitle: "{{env}} für den Checkout verwenden?",
				activateConfirmMessage:
					"Neue Bestellungen verwenden diese Umgebung, sobald der Schlüssel geprüft ist. Um zurückzuwechseln, kommen Sie hierher zurück und schalten stattdessen die andere ein.",
				activateConfirmButton: "Verwenden",
				saveButton: "Speichern & prüfen",
				verifyingLabel: "Wird geprüft…",
				closeButton: "Schließen",
				overwriteConfirmTitle: "Diesen Revolut-Secret-Key ersetzen?",
				overwriteConfirmMessage:
					"Wenn der Checkout diese Umgebung gerade verwendet, übernimmt der neue Schlüssel, sobald er geprüft ist.",
				overwriteConfirmButton: "Ersetzen",
			},
		},
		appleWallet: {
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			wizardAriaLabel: "Apple Wallet Pässe einrichten",
			certificateExpiresLabel: "Zertifikat läuft am {{date}} ab",
			wizard: {
				closeAriaLabel: "Schließen",
				continueButton: "Weiter",
				backButton: "Zurück",
				saveButton: "Speichern & prüfen",
				verifyingLabel: "Wird geprüft…",
				closeButton: "Schließen",
				setup: {
					title: "Einrichtung",
					description: "Vier Schritte, der Reihe nach",
					copied: "Kopiert",
					copyFailed:
						"Kopieren nicht möglich — Text markieren und manuell kopieren",
					copyAriaLabel: "Diesen Befehl kopieren",
					createId: {
						title: "Die ID bei Apple anlegen",
						body: "Description: beliebiger Name. Identifier: pass.de.ihrefirma.ticket",
						link: "Apple öffnen",
					},
					makeRequest: {
						title: "Die Signaturanfrage erzeugen — im Terminal ausführen",
					},
					getCertificate: {
						title: "Das Zertifikat bei Apple holen",
						body: "Unter Services „Pass Type ID Certificate“ wählen. Die eben angelegte ID auswählen, pass.certSigningRequest hochladen, dann Download.",
						link: "Apple öffnen",
					},
					buildFiles: {
						title: "Die beiden Dateien erzeugen — im Terminal ausführen",
					},
					expiryNote:
						"Apples Zertifikat gilt ein Jahr. Tragen Sie sich für Monat elf eine Erinnerung ein.",
				},
				credentials: {
					title: "Zertifikat & IDs",
					description: "Die beiden Dateien einfügen",
					passTypeIdLabel: "Pass Type ID",
					passTypeIdHint: "Die bei Apple registrierte Kennung.",
					teamIdLabel: "Team ID",
					teamIdHint: "Steht bei Apple neben Ihrem Firmennamen.",
					certificatePemLabel: "Zertifikat",
					certificatePemHint:
						"passcert.pem öffnen und den gesamten Inhalt einfügen.",
					certificatePemKeepHint:
						"Leer lassen, um das Gespeicherte zu behalten.",
					wwdrPemLabel: "Apple WWDR",
					wwdrPemHint: "wwdr.pem öffnen und den gesamten Inhalt einfügen.",
					wwdrPemKeepHint: "Leer lassen, um das Gespeicherte zu behalten.",
				},
				verify: {
					invalidTitle: "Dieses Zertifikatspaar hat nicht korrekt signiert",
					unreachableTitle: "Die Prüfung konnte nicht abgeschlossen werden",
					notAuthorizedTitle: "Apple hat dieses Zertifikat nicht akzeptiert",
					unknownTitle: "Etwas ist schiefgelaufen",
				},
				transportErrors: {
					unauthenticated: {
						title: "Ihre Sitzung ist abgelaufen",
						body: "Melden Sie sich erneut an und versuchen Sie es noch einmal — dieses Zertifikat wurde nicht getestet.",
					},
					notAuthorized: {
						title: "Sie haben keine Berechtigung, dies zu speichern",
						body: "Zum Speichern brauchen Sie zwei Berechtigungen: „Manage integrations“, um das Zertifikat zu speichern, und „Manage events“, um Pass Type ID und Team ID zu speichern. Bitten Sie einen Administrator um die fehlende — es wurde nichts gespeichert und dieses Zertifikat wurde nicht getestet.",
					},
					invalidInput: {
						title: "Diese Übermittlung konnte nicht gesendet werden",
						body: "Laden Sie die Seite neu und versuchen Sie es erneut — dieses Zertifikat wurde nicht getestet.",
					},
					unknown: {
						title: "Beim Speichern dieses Zertifikats ist etwas schiefgelaufen",
						body: "Versuchen Sie es gleich noch einmal — dieses Zertifikat wurde nicht getestet.",
					},
				},
				done: {
					title: "Fertig",
					description: "Gespeichert und geprüft",
					heading:
						"Dieses Zertifikatspaar wurde getestet, indem es einen echten Test-Pass signiert hat, und ist nun für diesen Workspace gespeichert.",
					certificateExpiresLabel:
						"Es läuft am {{date}} ab — planen Sie ein, es vorher zu ersetzen.",
				},
				overwriteConfirm: {
					certificate: {
						title: "Ihr Pass-Type-ID-Zertifikat ersetzen?",
						message:
							"Neue Pässe werden mit diesem Zertifikat signiert, sobald es geprüft ist. Das gespeicherte Apple-WWDR-Zertifikat bleibt unverändert.",
					},
					intermediate: {
						title: "Das gespeicherte Apple-WWDR-Zertifikat ersetzen?",
						message:
							"Neue Pässe werden mit diesem Apple-WWDR-Zertifikat signiert, sobald es geprüft ist. Das gespeicherte Zertifikat bleibt unverändert.",
					},
					both: {
						title: "Zertifikat und Apple-WWDR-Zertifikat ersetzen?",
						message:
							"Neue Pässe werden mit diesem Zertifikatspaar signiert, sobald es geprüft ist.",
					},
				},
				overwriteConfirmButton: "Ersetzen",
			},
		},
		googleWallet: {
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			wizardAriaLabel: "Google Wallet einrichten",
			wizard: {
				closeAriaLabel: "Schließen",
				continueButton: "Weiter",
				backButton: "Zurück",
				serviceAccountLabel: "Das Dienstkonto dieses Shops",
				copyServiceAccountAriaLabel: "Dienstkonto-E-Mail kopieren",
				copyServiceAccountSuccess: "Dienstkonto-E-Mail kopiert.",
				prerequisites: {
					title: "Voraussetzungen",
					description: "Zuerst in Googles eigener Konsole einrichten",
					intro:
						"Diese zwei Schritte erledigen Sie in Googles eigener Wallet-Konsole, nicht hier. Keiner davon blockiert „Weiter“ — erst der nächste Schritt „Prüfen“ zeigt wirklich, ob sie erledigt sind; wer vorspringt, bekommt dort einfach angezeigt, was noch fehlt.",
					issuerLabel: "Für diesen Shop gibt es einen Wallet-API-Aussteller",
					issuerWhy:
						"Öffnen Sie „Google Wallet API“ im linken Menü der Konsole — ignorieren Sie das lange Formular „Create an event ticket class“, das sich dabei öffnet; diese App legt Ticketklassen selbst an, eine pro Event. Die Issuer-ID selbst ist eine Einstellung dieses Shops — Sie geben sie nicht hier ein.",
					developerRoleLabel:
						"Das Dienstkonto dieses Shops hat Developer-Zugriff",
					developerRoleWhy:
						"Öffnen Sie in der Wallet-Konsole Users → Invite a user und gewähren Sie dem Dienstkonto, unter dem dieser Shop in Ihrem Google-Cloud-Projekt{{project}} läuft, Developer-Zugriff — die Prüfung (nächster Schritt) zeigt seine genaue Adresse. Wechselt dieser Shop auf ein neues Laufzeitkonto, laden Sie die neue Adresse vorher ein. Das ist der am häufigsten übersehene Schritt — und er passiert in der Wallet-Konsole, nicht in Google Cloud IAM.",
					demoModeNote:
						"Solange dieser Aussteller keinen Veröffentlichungszugriff hat, lässt sich ein Pass nur auf Google-Konten speichern, die auf derselben Users-Seite eingetragen sind. „Fertig“ (nach der Prüfung) führt Sie durch die Beantragung des Veröffentlichungszugriffs.",
					footerAdvisory:
						"Die Voraussetzungen sind unverbindlich — „Prüfen“ (als Nächstes) ist die eigentliche Kontrolle.",
				},
				verify: {
					title: "Prüfen",
					description: "Eine Live-Prüfung gegen Google Wallet",
					idleBody:
						"Noch nicht geprüft — dies prüft die eigene Identität dieses Shops live gegen Google Wallet. Dabei wird nur gelesen, nichts verändert — Sie können es also so oft ausführen, wie Sie möchten.",
					verifyButton: "Prüfen",
					noPermission:
						"Sie haben keine Berechtigung, Integrationen einzurichten.",
					pendingBody: "Ihre Einrichtung wird geprüft…",
					passedTitle: "Verbunden",
					passedBody: "Dieser Shop darf Google-Wallet-Pässe erstellen.",
					invalidTitle: "Kein Aussteller bestätigt",
					invalidBody:
						"Google hat für diesen Shop keinen Wallet-API-Aussteller bestätigt — entweder ist noch keiner eingerichtet, oder der eingerichtete existiert in der Konsole nicht. Das hat Google genau gemeldet:",
					notAuthorizedTitle: "Noch keine Berechtigung",
					notAuthorizedBody:
						"Google hat geantwortet, aber dieser Shop darf über diesen Aussteller noch keine Pässe ausstellen. Öffnen Sie in der Wallet-Konsole Users → Invite a user, gewähren Sie dem eigenen Dienstkonto dieses Shops Developer-Zugriff und versuchen Sie es erneut. Das hat Google genau gemeldet:",
					unreachableTitle: "Google Wallet nicht erreichbar",
					unreachableBody:
						"Die Anfrage erhielt keine Antwort — prüfen Sie Ihre Verbindung und versuchen Sie es erneut. Das hat Google genau gemeldet:",
					unknownTitle: "Etwas ist schiefgelaufen",
					unknownBody:
						"Die Prüfung ist nicht bestanden, und für diesen Ausgang gibt es hier keinen hinterlegten Schritt. Das hat Google genau gemeldet:",
					copyDetailAriaLabel: "Details kopieren",
					copyDetailSuccess: "Details kopiert.",
					copyDetailError:
						"Kopieren fehlgeschlagen — bitte den Text manuell markieren und kopieren.",
					retryButton: "Erneut versuchen",
				},
				done: {
					title: "Fertig",
					description: "Geprüft — noch ein Schritt, bis es live geht",
					heading: "Der Aussteller ist geprüft.",
					publishingAccessTitle:
						"Veröffentlichungszugriff — vor dem Livebetrieb erforderlich",
					publishingAccessBody:
						"Solange dieser Aussteller keinen Veröffentlichungszugriff hat, speichert Google Pässe nur auf Konten, die Sie auf der Users-Seite der Konsole eintragen, und kennzeichnet jeden Pass mit [TEST ONLY]. Der Veröffentlichungszugriff hebt beides auf — danach kann jedes Google-Konto einen Pass speichern, auch bereits ausgestellte.",
					step1:
						"Öffnen Sie die Google Pay & Wallet Console und wählen Sie links im Menü „Google Wallet API“.",
					step2:
						"Bestätigen Sie die beiden Voraussetzungen von Google: ein vollständiges Unternehmensprofil und mindestens eine Pass-Klasse — diese App legt eine automatisch an, sobald der erste Auftrag einen Pass ausstellt.",
					step3:
						"Öffnen Sie den Antrag und beschreiben Sie Ihren Anwendungsfall. Der Text unten beantwortet genau das — kopieren Sie ihn hinein.",
					useCaseLabel:
						"Ihre Antwort zum Anwendungsfall, fertig zum Einfügen (englisch, wie von Google geprüft)",
					useCase:
						"We are an event ticketing platform. When a buyer completes a purchase, we issue one Google Wallet event ticket pass per ticket bought, and give the buyer a save link on their order page and in their confirmation email.\n\nEach pass carries only what a guest needs at the door: the event name, the venue name and street address, the event start and end date and time, our organisation name and logo as the issuer, and a QR code that our door staff scan to admit the guest.\n\nThe QR code encodes a random token identifying that one ticket in our system. Admission is always decided by that ticket's current status in our own database, never by the pass, so a refunded or cancelled ticket is refused at the door even if the pass on the phone still looks valid.\n\nThe passes contain no personal data: no guest name, no email address, and no payment or order information.",
					copyUseCaseAriaLabel: "Antwort zum Anwendungsfall kopieren",
					copyUseCaseSuccess: "Antwort zum Anwendungsfall kopiert",
					reviewNote:
						"Google nennt 2–3 Werktage für die Prüfung; die Freigabe ist einseitig — beantragen Sie sie also, wenn Sie für echte Käufer bereit sind, und lieber früh als erst zum Livebetrieb.",
					openConsoleButton: "Google Pay & Wallet Console öffnen",
					smokeTestNote:
						"Solange der Antrag läuft, tragen Sie Ihr eigenes Google-Konto auf dieser Users-Seite ein, um selbst einen Pass zu testen.",
					closeButton: "Schließen",
				},
			},
		},
		gemini: {
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			wizardAriaLabel: "Gemini-KI-Generierung einrichten",
			form: {
				closeAriaLabel: "Formular für den Gemini-API-Schlüssel schließen",
				apiKeyLabel: "Gemini-API-Schlüssel",
				saveButton: "Speichern & prüfen",
				verifyingLabel: "Wird geprüft…",
				closeButton: "Schließen",
			},
		},
		metaAds: {
			loadingAnnouncement: "Meta-Werbeverbindung wird geprüft",
			configureButton: "Einrichten",
			manageButton: "Verwalten",
			wizardAriaLabel: "Meta-Werbung einrichten",
			form: {
				wizard: {
					title: "Meta",
					accountSavedTitle: "Werbekonto gespeichert",
					continueVerification: "Prüfung fortsetzen",
					stepSetup: "Vorbereiten",
					stepValues: "Verbinden",
					stepDone: "Fertig",
					continueButton: "Ich habe die Angaben",
					setupAgain: "Einrichtungsanleitung",
					replace: "Ersetzen",
					stored: "Gespeichert",
					keepStored: "Gespeicherte Zugangsdaten behalten",
					scopeLabel: "Meta verwenden für",
					measurementLabel: "Käufe melden",
					adsLabel: "Käufe melden und Anzeigen schalten",
					lockedNote:
						"Anzeigen sind aktiviert. Eine geleerte Konto-ID entfernt das Konto nicht.",
					confirmTitle: "Anzeigen aktivieren?",
					confirmMessage:
						"Dieses Werbekonto bleibt verbunden. Weitere Prüfungen benötigen Ihre Rechtsidentität und eine Zahlungsquelle im Werbekonto.",
					confirmButton: "Anzeigen aktivieren",
					discardTitle: "Änderungen verwerfen?",
					discardButton: "Verwerfen",
					keepEditing: "Weiter bearbeiten",
					doneTitle: "Meta ist verbunden",
					doneMeasurement: "Bezahlte Käufe werden an Ihr Pixel gemeldet.",
					doneAds:
						"Die Kaufmeldungen sind verbunden. Anzeigen können Sie in der Event-Konsole starten.",
					firstAccountNote:
						"Das Werbekonto ist gespeichert. Speichern und prüfen Sie erneut, um die Zahlungsquelle und Ihre Rechtsidentität zu prüfen.",
					prerequisites:
						"Sie benötigen ein Business-Portfolio und eine Facebook-Seite. Verbinden Sie für Instagram-Anzeigen ein professionelles Instagram-Konto.",
					businessSettings: "Business Settings öffnen",
					appTitle: "Eine App erstellen",
					appBody:
						"Erstellen Sie eine Meta-App mit dem Marketing-API-Use-Case. Kopieren Sie die App-ID und das App-Geheimnis.",
					appLink: "App erstellen",
					portfolioTitle: "Assets hinzufügen",
					portfolioBody:
						"Fügen Sie in den Business Settings Ihre App, Ihr Werbekonto, Ihre Facebook-Seite und Ihr Instagram-Konto demselben Portfolio hinzu. Kopieren Sie die IDs für Ihre Anzeigen.",
					portfolioLink: "Business Settings öffnen",
					pixelTitle: "Ein Pixel erstellen",
					pixelBody:
						"Verbinden Sie im Events Manager eine Web-Datenquelle. Erstellen Sie das Pixel und kopieren Sie dessen ID.",
					pixelLink: "Events Manager öffnen",
					tokenTitle: "Ein System-User-Token erzeugen",
					tokenBody:
						"Weisen Sie unter Business Settings → Users → System users die App und die Assets zu. Erzeugen Sie ein Token mit den Berechtigungen für Kaufmeldungen und gegebenenfalls Anzeigen.",
					tokenLink: "System users öffnen",
					wrongToken:
						"Verwenden Sie das System-User-Token aus den Business Settings. Ein Token nur aus dem Events Manager kann Käufe melden, aber keine Anzeigen schalten.",
				},
				closeAriaLabel: "Formular für Meta-Werbung schließen",
				credentialsHeading: "Zugangsdaten",
				credentialsNote: "Verwenden Sie Token und Geheimnis Ihrer Meta-App.",
				accessTokenLabel: "Conversions-API-Zugriffstoken",
				accessTokenHint: "Aus Business Settings → Users → System users.",
				accessTokenKeepHint: "Das gespeicherte System-User-Token ersetzen.",
				appSecretLabel: "App-Geheimnis",
				appSecretHint: "Aus dem App-Dashboard, neben der App-ID.",
				appSecretKeepHint: "Das gespeicherte App-Geheimnis ersetzen.",
				credentialPasteError:
					"Als eine Zeile einfügen — keine Leerzeichen, keine Zeilenumbrüche.",
				measurementHeading: "Was gemessen wird",
				measurementNote: "Das Pixel für Ihre Kaufmeldungen.",
				pixelIdLabel: "Meta Pixel-ID",
				pixelIdHint: "Aus Events Manager → Data sources.",
				pixelIdKeepHint: "Leeren behält die gespeicherte Pixel-ID.",
				pixelIdFormatError:
					"Nur die Nummer aus dem Events Manager — ausschließlich Ziffern, sonst nichts.",
				advertiserHeading: "Wo Anzeigen laufen",
				advertiserNote: "Konten und Identitäten für Anzeigen.",
				appIdLabel: "Meta-App-ID",
				appIdHint: "Aus Ihrem Meta-App-Dashboard.",
				adAccountIdLabel: "Werbekonto-ID",
				adAccountIdHint: "Aus Business Settings → Accounts → Ad accounts.",
				adAccountIdFormatError:
					"Diese trägt Metas act_-Präfix — act_ gefolgt von der Kontonummer. Sehen Sie nur die Nummer allein, stellen Sie act_ voran.",
				pageIdLabel: "Facebook-Seiten-ID",
				pageIdHint: "Die Facebook-Seite in Ihrem Business-Portfolio.",
				instagramAccountIdLabel: "Instagram-Konto-ID",
				instagramAccountIdHint:
					"Aus Business Settings → Accounts → Instagram accounts.",
				numericIdFormatError:
					"Nur Ziffern — übernehmen Sie die Nummer, die Meta anzeigt, ohne etwas darum herum.",
				saveButton: "Speichern & prüfen",
				verifyingLabel: "Wird geprüft…",
				closeButton: "Schließen",
			},
		},
	},
	analytics: {
		rangeToday: "Heute",
		range7d: "7 Tage",
		range30d: "30 Tage",
		rangeCustom: "Eigener Zeitraum",
		computing: "wird aktualisiert …",
		asOfCached: "Stand {{time}}",
		asOfFresh: "Stand {{time}}",
		refresh: "Auswertung aktualisieren",
		campaignScope: "Eine Kampagne",
		campaignScopeClear: "Wieder alle Kampagnen zeigen",
		busyAnnouncement: "Auswertung wird aktualisiert",
		readyAnnouncement: "Auswertung ist aktuell",
		kpiDaysToGo: "Tage bis Start",
		kpiVisits: "Besuche",
		kpiConversion: "Kaufquote",
		kpiRevenue: "Umsatz",
		kpiDwell: "Typische Besuchsdauer",
		kpiReturning: "Wiederkehrende Besucher",
		deltaWithinRange: "zweite Hälfte des Zeitraums im Vergleich zur ersten",
		trendVisits: "Besuche pro Tag",
		trendRevenue: "Umsatz pro Tag",
		gapRule: "Ein Besuch endet nach {{minutes}} Minuten ohne Aktivität",
		kpiVisitors: "Besucher",
		legendPaidShare: "davon gekauft",
		retrievalCollections: "Nach Format",
		retrievalAllVia: "Tickets aufbewahrt, alle als {{channel}}",
		funnelTitle: "Vom Warenkorb zum Ticket",
		landingClickThrough:
			"{{through}} von {{landing}} Besuchen der Startseite gingen weiter auf eine Veranstaltungsseite ({{percent}} %)",
		stoppedOnLanding: "{{count}} blieben auf der Startseite",
		stageEventPage: "Warenkorb begonnen",
		stageContact: "Kontaktdaten ausgefüllt",
		stageAuthorising: "Zahlung gestartet",
		stagePaid: "Bezahlt",
		droppedOut: "abgesprungen",
		funnelCoveragePartial:
			"Für diesen Zeitraum konnten nicht alle Daten gelesen werden. Die gezählten Schritte sind deshalb ungefähre Werte.",
		funnelCoverageUnavailable:
			"Für einen Teil dieses Zeitraums lassen sich diese Schritte nicht ermitteln. Deshalb wird hier nichts gezählt — lesen Sie sie als unbekannt, nie als null.",
		funnelCoverageCountedDays:
			"Gezählt über die {{counted}} von {{total}} Tagen, die sich ermitteln lassen. Der Rest dieses Zeitraums wird hier nicht gezählt — lesen Sie diese Tage als unbekannt, nie als null.",
		figureApproximate: "rund {{value}}",
		conversionCoverageIncomplete: "nicht ermittelbar",
		outcomesTitle: "Warum daraus keine Tickets wurden",
		clientSpecsTitle: "Geräte und Browser der Besucher",
		clientSpecsEmpty:
			"Für diesen Zeitraum wurden noch keine Gerätedaten gemessen.",
		clientOs: "Betriebssystem",
		clientBrowser: "Browser",
		clientDevice: "Gerätetyp",
		clientViewport: "Bildschirmgröße",
		clientOrientation: "Bildschirmausrichtung",
		clientFiltersTitle: "Aktive Besucherfilter",
		clientFilterClear: "{{filter}} entfernen",
		clientCoverageComplete: "Gerätedaten sind vollständig abgedeckt.",
		clientCoveragePartial:
			"Einige Kennzahlen für den Client-Filter sind unvollständig.",
		clientCoverageUnavailable:
			"Client-gefilterte Kennzahlen sind für einen Teil dieser Auswertung nicht verfügbar.",
		clientValues: {
			ios: "iPhone / iPad",
			android: "Android",
			macos: "macOS",
			windows: "Windows",
			linux: "Linux",
			chromeos: "ChromeOS",
			safari: "Safari",
			chrome: "Chrome",
			firefox: "Firefox",
			edge: "Edge",
			samsung: "Samsung Internet",
			phone: "Telefon",
			tablet: "Tablet",
			desktop: "Desktop",
			xs: "Sehr klein",
			sm: "Klein",
			md: "Mittel",
			lg: "Groß",
			xl: "Sehr groß",
			portrait: "Hochformat",
			landscape: "Querformat",
			other: "Sonstige",
		},
		outcomeSoldOut: "ausverkauft",
		outcomeNotEligible: "nicht berechtigt",
		outcomeInternal: "technische Störung",
		outcomeLicenceReadOnly: "Lizenz abgelaufen",
		outcomeAbandoned: "liegen gelassen",
		outcomeExpired: "abgelaufen",
		campaignsTitle: "Woher die Besuche kommen",
		colChannel: "Kanal",
		colVisits: "Besuche",
		colCheckouts: "Warenkörbe",
		colPaid: "Bezahlt",
		colConversion: "Kaufquote",
		channelDirect: "Direkt",
		campaignsEmpty:
			"Im gewählten Zeitraum gab es keine Besuche — deshalb auch keine Kanäle.",
		// ── Die bezahlten Kampagnen: Kosten neben Verkäufen ──────────────────
		// Ein anderes Thema als der Kanalblock darüber, der jeden Besuch nach
		// seiner Quelle zählt und von ausgegebenem Geld nichts weiß.
		costTitle: "Was die Werbung gekostet hat",
		costSubtitle:
			"Jeweils der gesamte Lauf einer Kampagne — nicht der oben gewählte Zeitraum. Metas eigene Zahlen stehen neben dem, was dieser Shop tatsächlich gesehen hat, und beide werden nie zu einer Zahl verrechnet.",
		costSpent: "Ausgegeben",
		costPerTicket: "Pro verkauftem Ticket",
		costPerTicketNone: "noch nichts verkauft",
		costTicketsSold: "Verkaufte Tickets",
		costRunOpen: "Läuft seit {{span}}",
		costRunClosed: "Lief {{span}}",
		costRunUnstamped: "Start unbekannt",
		costScopeOnly: "Nur diese Kampagne",
		costMetaClicks: "Meta hat {{clicks}} Klicks gezählt · gelesen {{time}}",
		costObservedVisits:
			"Davon sind {{visitors}} hier angekommen und {{orders}} haben gekauft · gelesen {{time}}",
		// Die vier Verkehrsurteile. Drei davon lassen jede Zahl daneben auf null
		// stehen, und nur eines dieser drei ist ein Fehler — ohne diese Zeilen
		// sehen eine stille Kampagne und eine kaputte Zuordnung gleich aus.
		trafficUnreported: "Noch keine Zahlen von Meta",
		trafficUnreportedWhy:
			"Meta hat zu dieser Kampagne noch nichts gemeldet. Die leeren Zahlen daneben sind kein Ergebnis — es gibt bisher nichts zu vergleichen.",
		trafficNone: "Noch ist niemand gekommen",
		trafficNoneWhy:
			"Meta hat die Anzeige ausgespielt, geklickt hat sie bisher niemand. Die leeren Zahlen daneben stimmen.",
		trafficArriving: "Besuche werden zugeordnet",
		trafficArrivingWhy:
			"Metas Klicks und die hier ankommenden Besuche passen zusammen — jede Zahl daneben trägt.",
		trafficUntraceable: "Diese Klicks kommen bei uns nicht an",
		trafficUntraceableWhy:
			"Meta hat Klicks gezählt, und kein einziger hier ankommender Besuch ließ sich dieser Kampagne zuordnen. Das Ausgegebene ist echt; alles, was hier daran gemessen wird, nicht — lesen Sie es als unbekannt, nie als null.",
		retrievalTitle: "Wie Käufer ihr Ticket aufbewahren",
		retrievalSubtitle:
			"In welchem Format sie es gespeichert haben — PDF-Download, Apple/Google Wallet, Kalenderdatei oder an jemand anderen weitergegeben.",
		retrievalDownload: "PDF geladen",
		retrievalWalletApple: "Apple Wallet",
		retrievalWalletGoogle: "Google Wallet",
		retrievalIcs: "Kalender (.ics)",
		retrievalHandover: "Weitergegeben",
		retrievalEmpty:
			"Im gewählten Zeitraum hat noch niemand ein Ticket aufbewahrt.",
		browserBias: "Wer mit Handy und Laptop kommt, zählt als zwei Besucher.",
		presence: {
			onEvent: "gerade bei diesem Event",
			elsewhere: "{{count}} woanders im Shop",
			nobody: "Gerade niemand im Shop",
		},
		drill: {
			openSources: "Details ansehen",
			crumbOrders: "Bestellungen",
			crumbTickets: "Tickets",
			crumbSources: "Quellen",
			crumbLimit: "Grenze",
			closeAll: "Alle offenen Ebenen schließen",
			partsHeader: "{{count}} Teile",
			partsEmpty: "In diesem Zeitraum gibt es hier nichts aufzuteilen",
			openedFrom: "Gesamt",
			partsShort:
				"Die Teile ergeben {{sum}} und damit nicht die Gesamtzahl darüber.",
			attributionParts: "Woher diese Zahl kommt",
			titleVisitsByWeek: "Besuche, nach Woche",
			titleVisitsByDay: "Besuche, nach Tag",
			titleVisitsByCampaign: "Besuche am {{day}}, nach Kampagne",
			titleRevenueByWeek: "Umsatz, nach Woche",
			titleRevenueByDay: "Umsatz, nach Tag",
			titleBrowsers: "Besucher, neu und wiederkehrend",
			titleChannel: "{{channel}} — was der Kanal gebracht hat",
			titleChannelOnDay: "{{channel}} am {{day}}",
			titleDropPayment: "Zahlung gestartet, nie bezahlt",
			titleStageByWeek: "{{stage}}, nach Woche",
			titleStageByDay: "{{stage}}, nach Tag",
			claimVisitsWeeks:
				"Eine Woche je Zeile, jede öffnet ihre eigenen Tage. Zusammen ergeben sie genau den gewählten Zeitraum.",
			claimVisitsDays:
				"Ein Tag je Zeile. Zusammen ergeben sie genau den gewählten Zeitraum.",
			claimVisitsCampaign:
				"Die Kampagnen und die direkten Besuche ergeben zusammen die Besuche des Tages — nichts fehlt.",
			claimChannel:
				"Jeder Besuch dieses Kanals steht in genau einer der drei Zeilen — ein bezahlter Warenkorb steckt in der ersten und wird in der zweiten nie noch einmal gezählt.",
			claimBrowsers:
				"Jeder Besucher an einem Tag ist entweder neu oder wiederkehrend, beide zusammen ergeben also den Tag — und wer an drei Tagen da war, zählt an jedem davon.",
			claimRevenue:
				"Immer der aktuelle Wert — eine Erstattung senkt, was ein vergangener Tag eingebracht hat, und diese Zahl folgt dem.",
			claimDropPayment:
				"Diese fünf sind die ganze Lücke zwischen gestarteter und bezahlter Zahlung — ein Warenkorb, der bis zur Zahlung kam und nicht bezahlt hat, hat genau eines davon getan.",
			claimStage:
				"Ein Tag je Zeile: die von Käufern selbst begonnenen Warenkörbe, die diese Stufe erreicht haben. Ein von Ihnen ausgestellter Warenkorb ist kein Ergebnis eines Besuchs und steckt in keiner davon.",
			titleProvenance: "{{subject}} — woher diese Zahl kommt",
			provDerivation: "Die Zahl",
			provRange: "Zeitraum",
			provRangeDays: "{{from}} – {{to}}, {{days}} Tage",
			provGap: "Pause zwischen Besuchen",
			provScope: "Enthalten",
			provScopeAll: "Alle Kampagnen dieser Veranstaltung",
			provScopePlus: "{{others}} · {{campaign}}",
			provComputedAt: "Zuletzt aktualisiert",
			provComputedCached: "{{time}}",
			provComputedFresh: "{{time}}",
			provSourcesTitle: "Grundlage",
			provSourceOrders: "Die bezahlten Bestellungen, für den Umsatz.",
			provSourceCacheUsed: "Abgeschlossene Tage, bereits zusammengezählt.",
			provSourceCacheUnused:
				"Nicht verwendet — bei dieser Pause wird jeder Tag neu gezählt.",
			provFreshnessTitle: "Wie aktuell",
			provFreshnessCount:
				"{{stored}} von {{total}} Tagen bereits zusammengezählt",
			provFreshnessAllFresh: "Alle {{total}} Tage neu gezählt",
			provFreshnessDay: "{{day}} — neu gezählt",
			provSensitivityTitle: "Wenn ein Besuch nach einer anderen Pause endete",
			provSensitivityLead: "Dieselben Besuche, anders zusammengefasst.",
			provSensitivityCurrent: "in Verwendung",
			provDocuments: "{{count}} Einträge",
			provDocumentsTimeline:
				"{{pageViews}} Seitenaufrufe · {{basketChanges}} Warenkorb-Änderungen · {{orderActions}} aufbewahrte Tickets · {{checkoutStages}} erreichte Stufen",
			provDocumentsNone: "Nicht verwendet",
			provNeighbourTitle: "Warum das von der Zahl daneben abweicht",
			provVisitsClaim:
				"Besuche stehen auf {{visits}}, Besucher auf {{visitors}}.",
			provVisitsBecause:
				"Ein Besucher kann an einem Tag mehrmals wiederkommen: zwei Phasen mit mehr als der eingestellten Pause dazwischen sind zwei Besuche eines Besuchers. Keine der beiden Zahlen ist falsch — sie zählen Verschiedenes.",
			provVisitorsClaim:
				"Besucher werden je Tag gezählt und über den Zeitraum addiert.",
			provVisitorsBecause:
				"Wer an drei Tagen da war, zählt hier dreimal. Das ist die Summe der Besucher je Tag, nicht die Zahl verschiedener Personen im ganzen Zeitraum.",
			provDwellClaim:
				"Das ist der typische Besuch eines typischen Tages, nicht der typische Besuch im ganzen Zeitraum.",
			provDwellBecause:
				"Jeder Tag hält nur seinen eigenen typischen Besuch fest, nicht jeden einzelnen Besuch dahinter — die Tage lassen sich also nicht zu einer Zahl zusammenlegen. Das ist die Zahl, für die die Auswertung geradestehen kann, und sie sagt, welche es ist.",
			provBasketClaim:
				"Warenkörbe stehen auf {{baskets}}, Besuche auf {{visits}} — die erste Zahl darf die zweite übersteigen.",
			provBasketBecause:
				"Ein Warenkorb entsteht in dem Moment, in dem er nicht mehr leer ist, und ist damit keine Teilmenge der Besuche daneben: wer über einen Direktlink wiederkommt, legt einen weiteren Warenkorb an, ohne einen weiteren Besuch zu erzeugen. Genau deshalb beginnt der Trichter beim Warenkorb und nicht beim Besuch.",
			provRevenueClaim:
				"Bezahlte Warenkörbe stehen auf {{paid}} — der Umsatz daneben kann fallen, ohne dass diese Zahl fällt.",
			provRevenueBecause:
				"Der Trichter zählt Warenkörbe zu dem Zeitpunkt, an dem sie bezahlt wurden. Der Umsatz folgt den Bestellungen: eine Tage später gebuchte Erstattung senkt den Umsatz dieses Tages, der bezahlte Warenkorb bleibt stehen.",
			attributionProvenance: "Jede Zahl hier wird auf dieselbe Weise ermittelt",
			doorHowDerived: "Woher diese Zahl kommt",
			titleOrdersOfDay: "Bezahlte Bestellungen am {{day}}",
			titleOrdersOfChannel: "{{channel}} — Bestellungen am {{day}}",
			titleTickets: "Tickets aus Rechnung {{invoice}}",
			recordsLeadOrders: "Die Bestellungen hinter dem Geld.",
			recordsLeadTickets: "{{buyer}} · {{total}}",
			recordsBoundOrders: "Zeigt {{shown}} von {{total}} Bestellungen",
			recordsBoundOrdersTop:
				"Zeigt {{shown}} von {{total}} Bestellungen, die größten zuerst",
			recordsBoundTicketsOne: "1 Ticket, alle davon",
			recordsBoundTickets: "{{count}} Tickets, alle davon",
			recordsOrderDetailOne: "{{tickets}} Ticket · Rechnung {{invoice}}",
			recordsOrderDetail: "{{tickets}} Tickets · Rechnung {{invoice}}",
			recordsStatusPaid: "bezahlt",
			recordsStatusRefunded: "erstattet",
			recordsStatusValid: "gültig",
			recordsStatusRedeemed: "eingelöst",
			recordsAdmitted: "eingelassen {{time}} · {{door}}",
			recordsNotAdmitted: "noch nicht eingelassen",
			recordsDoorUnknown: "Tür unbekannt",
			doorTheOrders: "Die Bestellungen dieses Tages",
			attributionRecords: "Direkt aus den Bestellungen",
			wallPrivacyTitle: "Hier ist Schluss",
			wallPrivacyLead:
				"Eine Summe öffnet sich in eine kleinere Summe. Sie öffnet sich nicht in einen Menschen.",
			wallPrivacyRefusal:
				"Der Ticketshop zählt seine Besucher, ohne jemanden um Erlaubnis zu fragen, und das trägt nur, solange es eine Zählung bleibt. Eine Summe darf sich in kleinere Summen öffnen und zeigen, woher sie kommt. Sie öffnet sich nie in den Weg eines einzelnen Besuchers durch den Shop, und es gibt keine Einstellung, die das erlaubt.",
			wallBoundedTitle: "Absichtlich zu einer Zeile gefaltet",
			wallBoundedLead:
				"Diese Zeile wurde nie einzeln aufgeschrieben, also gibt es darin nichts zu öffnen.",
			wallBoundedRefusal:
				"Jeder Tag führt seine zwanzig größten Kampagnen namentlich und fasst den Rest in dieser einen Zeile zusammen, ob es vier Kampagnen waren oder viertausend. Was in dieser Zeile steckt, wurde nie einzeln festgehalten — es wird nicht zurückgehalten.",
			wallDotLabel: "Hier endet der Weg",
			wallInstead: "Was sie Ihnen stattdessen zeigt",
			doorSplitByCampaign: "Dieselbe Zahl, nach Kampagne aufgeteilt",
			doorNamedCampaigns: "Die namentlich geführten Kampagnen",
			attributionBoundary: "Was diese Auswertung zeigen kann, und warum",
			partPaid: "Warenkorb begonnen und bezahlt",
			partUnpaid: "Warenkorb begonnen, nicht bezahlt",
			partLeft: "ohne Warenkorb wieder gegangen",
			partFirstSeen: "erster Besuch an diesem Tag",
			partReturning: "schon einmal da",
		},
		reportFailed:
			"Die Auswertung konnte gerade nicht berechnet werden. Bitte erneut versuchen.",
	},
	campaign: {
		nav: "Werbung",
		offer: {
			title: "Mehr Leute auf Ihr Event bringen",
			standing:
				"{{sold}} von {{quota}} Tickets verkauft. Der Verkauf läuft noch {{days}} Tage.",
			what: "Ihr Event läuft auf Instagram und Facebook, bei Leuten in der Nähe. Wir schreiben den Text und machen das Bild. Sie sehen alles, bevor etwas ausgegeben wird.",
			noSale:
				"Es sind keine Tickets im Verkauf, also gibt es nichts zu bewerben.",
			notConfiguredTitle: "Werbung ist noch nicht eingerichtet",
			notConfiguredAdAccount:
				"Dieser Shop hat noch kein Werbekonto, also gibt es nichts, worüber die Anzeigen laufen könnten.",
			notConfiguredSpendingLimit:
				"Dieser Shop hat ein Werbekonto, aber keine Ausgabengrenze für Werbung. Ohne sie kann nichts starten.",
			notConfiguredBoth:
				"Diesem Shop fehlen ein Werbekonto und eine Ausgabengrenze für Werbung. Beides muss da sein, bevor etwas laufen kann.",
			notConfiguredWho: "Das richtet eine Administratorin ein.",
			notConfiguredCta: "Werbekonto einrichten",
			budgetLabel: "Wie viel möchten Sie ausgeben?",
			budgetHint: "Für die ganze Laufzeit, nicht pro Tag.",
			ceiling: "Dieser Shop erlaubt bis zu {{amount}} für Werbung.",
			perDay: "rund {{amount}} pro Tag über {{days}} Tage",
			worth: "ungefähr der Erlös aus {{count}} Tickets",
			cta: "Vorschlag ansehen",
		},
		drafting: {
			title: "Wir bauen Ihren Vorschlag",
			body: "Wir schreiben den Text und machen die Bilder aus Ihrem Plakat. Etwa eine halbe Minute — Sie können die Seite verlassen.",
		},
		failed: {
			title: "Wir konnten die Anzeige nicht einrichten",
			body: "Es wurde nichts ausgegeben. Versuchen Sie es noch einmal.",
			retry: "Noch einmal versuchen",
			reason: {
				notConfigured:
					"In den Einstellungen fehlt etwas: ein Werbekonto, eine Facebook-Seite, eine Ausgabengrenze oder Ihre rechtlichen Angaben. Eine Administratorin trägt es ein, dann noch einmal versuchen.",
				budgetOutOfBounds:
					"Ihr Betrag liegt unter Metas Mindestbetrag für dieses Werbekonto oder über der Grenze dieses Shops. Nehmen Sie einen anderen Betrag, oder lassen Sie die Grenze anheben.",
				worldMoved:
					"Das günstigste Ticket hat sich geändert, der Preis in der Anzeige stimmt nicht mehr. Bauen Sie einen neuen Vorschlag.",
				creativeUnusable:
					"Das Bild lässt sich nicht verwenden. Bauen Sie einen neuen Vorschlag und wählen Sie ein anderes.",
				deadCredential:
					"Meta akzeptiert das Zugriffstoken dieses Shops nicht mehr. Verbinden Sie Meta in den Einstellungen neu, dann noch einmal versuchen.",
				notPermitted:
					"Meta lässt dieses Token für Ihr Werbekonto oder Ihre Facebook-Seite nicht zu. Geben Sie ihm im Meta Business Manager Zugriff, dann noch einmal versuchen.",
				adReview:
					"Meta hat sich die Anzeige angesehen und lässt sie nicht laufen. Bauen Sie einen neuen Vorschlag mit anderem Text oder anderem Bild.",
				advertisingBlocked:
					"Meta lässt diesen Shop keine Anzeigen mehr schalten. An der Kampagne, am Bild oder am Betrag gibt es nichts zu ändern — das lässt sich nur bei Meta klären, über deren eigene Prüfung.",
				throttled:
					"Dieses Werbekonto hat in dieser Stunde alles ausgegeben, was Meta erlaubt. Es ist nichts falsch — versuchen Sie es später.",
				unclassified:
					"Meta hat abgelehnt und sagt nicht, warum; wir können Ihnen deshalb nicht sagen, was zu ändern ist. Versuchen Sie es noch einmal; wenn es weiter fehlschlägt, bauen Sie einen neuen Vorschlag.",
			},
		},
		refusal: {
			nothingPubliclyPurchasable:
				"Kein Ticket ist öffentlich im Verkauf, eine Anzeige hätte also kein Ziel.",
			workspaceLegalMissing:
				"Meta zeigt zu jeder Anzeige, wer sie bezahlt. Tragen Sie zuerst Ihre rechtlichen Angaben in den Einstellungen ein.",
			adSpendCeilingUnset:
				"Dieser Shop hat keine Ausgabengrenze für Werbung. Eine Administratorin setzt sie in den Einstellungen.",
			budgetOverCeiling:
				"Das ist mehr, als dieser Shop erlaubt. Nehmen Sie einen kleineren Betrag, oder lassen Sie die Grenze anheben.",
			budgetUnderMetaMinimum:
				"Meta braucht mindestens {{amount}} auf diesem Werbekonto.",
		},
		creative: {
			heading: "Das Bild",
			posterGroup: "Ihr Plakat",
			posterGroupWhy: "Gehört schon zu diesem Event.",
			uploadGroup: "Ihr eigenes Bildmaterial",
			uploadGroupWhy: "Läuft genau so, wie Sie es gemacht haben.",
			generatedGroup: "Von uns erzeugt",
			generatedGroupWhy: "Aus Ihrem Plakat und den Angaben zum Event.",
			uploadDrop: "Eigenes Bildmaterial hierher ziehen, einfügen oder klicken.",
			uploadSending: "{{name}} wird gesendet",
			uploadPreparing: "{{name}} wird verarbeitet",
			uploadLanded: "{{name}} steht zur Auswahl.",
			uploadSlow: "Das ist nicht angekommen.",
			uploadRetry: "Erneut senden",
			uploadRefused:
				"{{name}} konnte nicht gelesen werden. Versuchen Sie es mit JPEG, PNG oder WebP.",
			uploadSmall:
				"{{w}} × {{h}} — schmaler als {{min}} px. Es läuft trotzdem, kann aber unscharf wirken.",
			uploadUnderMetaMinimum:
				"{{w}} × {{h}} — unter Metas Mindestbreite von {{min}} px, deshalb übernimmt Meta es nicht. Wählen Sie ein anderes Bild, oder senden Sie eine breitere Datei.",
			aiBadge: "KI-generiert",
			aiNote:
				"Dieses Bild ist von einer KI. Wir kennzeichnen es in der Anzeige.",
			uploadNote:
				"Ihr Bild läuft ohne KI-Kennzeichnung. Wenn eine KI es gemacht hat, entscheiden Sie selbst, ob das dazugehört.",
			promptSummary: "Der Text, den wir benutzt haben",
			promptLede:
				"Kopieren Sie ihn in Ihr eigenes Programm, oder geben Sie ihn Ihrer Designerin.",
			promptCopy: "Text kopieren",
			promptCopied: "Text kopiert",
			none: "Noch kein Bild.",
		},
		proposal: {
			title: "Ihr Vorschlag",
			lede: "Nichts läuft schon. Ändern Sie, was Sie möchten, und geben Sie es dann frei.",
			staleTitle: "Dieser Vorschlag ist von {{date}}",
			staleBody:
				"Preise und Termine können sich seitdem geändert haben. Prüfen Sie sie vor der Freigabe.",
			copyHeading: "Was dabei steht",
			copyBoth: "Die Anzeige läuft auf Deutsch und auf Englisch.",
			headlineLabel: "Überschrift",
			bodyLabel: "Text",
			audienceHeading: "Wer sie sieht",
			audienceSentence:
				"Menschen ab {{age}} Jahren im Umkreis von {{radius}} km um {{place}}.",
			radiusLabel: "Umkreis",
			ageLabel: "Ab",
			scheduleHeading: "Wie lange",
			scheduleSentence: "Ab Freigabe bis {{end}} — {{days}} Tage.",
			scheduleWhy: "Endet, wenn Ihr letztes Ticket aus dem Verkauf geht.",
			costHeading: "Was es kostet",
			costWho: "Meta bucht von Ihrem eigenen Werbekonto ab, nicht von uns.",
			confirm: "{{amount}} freigeben",
		},
		confirm: {
			title: "{{amount}} freigeben?",
			subtitle:
				"Meta bucht von Ihrem eigenen Werbekonto bis zu {{amount}} ab — nie mehr.",
			message:
				"Meta prüft die Anzeige zuerst, meist innerhalb eines Tages. Ausgegeben wird nichts, bevor sie läuft. Sie können jederzeit pausieren; ausgegebenes Geld kommt nicht zurück.",
			ok: "Jetzt {{amount}} freigeben",
			cancel: "Zurück",
		},
		status: {
			building: {
				label: "Die Anzeige wird bei Meta eingerichtet",
				body: "Ausgegeben wird nichts, bevor Meta geprüft hat.",
			},
			submitted: {
				label: "Meta prüft die Anzeige",
				body: "Meist innerhalb eines Tages. Bis dahin ist nichts ausgegeben.",
				action: "Zurückziehen",
			},
			live: {
				label: "Läuft",
				body: "Ihre Anzeige ist auf Instagram und Facebook zu sehen.",
				action: "Pausieren",
			},
			rejected: {
				label: "Meta hat die Anzeige nicht freigegeben",
				body: "Metas eigener Wortlaut steht unten. Es läuft nichts weiter.",
				action: "Kampagne beenden",
			},
			paused: {
				label: "Pausiert",
				body: "Es wird nichts mehr ausgegeben. Der Rest Ihres Geldes ist noch da.",
				action: "Weiterlaufen lassen",
				endAction: "Kampagne beenden",
			},
			ended: {
				label: "Beendet",
				body: "Ihre Anzeige ist gelaufen.",
			},
			spentOf: "{{spent}} von {{total}} ausgegeben",
			daysLeft: "noch {{days}} Tage",
			metaSays: "Meta nennt das: {{status}}",
			learning: "Meta lernt noch, wem es die Anzeige zeigt: {{stage}}",
			window: "Ausgaben gezählt über Metas Zeitfenster {{window}}.",
		},
		rejection: {
			nothingSpent: "Es wurde nichts ausgegeben.",
			verbatim: "Metas eigener Wortlaut",
		},
		result: {
			tooEarly:
				"Noch keine Zahlen. Die ersten kommen von Meta nach ein paar Stunden.",
		},
		ad: {
			sponsored: "Gesponsert",
			cta: "Tickets sichern",
		},
		toast: {
			proposeFailed:
				"Der Vorschlag ließ sich gerade nicht bauen. Versuchen Sie es gleich noch einmal.",
			proposalEditFailed:
				"Änderungen konnten nicht gespeichert werden. Bitte erneut versuchen.",
			launchFailed:
				"Die Freigabe ist nicht durchgegangen. Es wurde nichts ausgegeben.",
			priceFloorMoved:
				"Das günstigste Ticket hat sich geändert, der Preis in der Anzeige stimmt nicht. Bauen Sie einen neuen Vorschlag.",
			proposalStale:
				"Dieser Vorschlag steht schon eine Weile, das Event kann sich bewegt haben. Bauen Sie einen neuen.",
			stopFailed:
				"Das hat Meta nicht erreicht. An Ihren Ausgaben ändert sich nichts — bitte erneut versuchen.",
			intentDoesNotApply:
				"Diese Kampagne ist gerade nicht in einem Zustand, in dem das geht.",
		},
	},
	onboarding: {
		close: "Zurück zu den Einstellungen",
		reopen: "Einrichtung",
		continue: "Weiter",
		skip: "Später",
		noAnswer:
			"Noch keine Antwort – möglicherweise wurde trotzdem gespeichert. Bitte prüfen Sie das, bevor Sie es erneut versuchen.",
		completed: "Erledigt: {{steps}}.",
		skipped: "Übersprungen: {{steps}}.",
		steps: {
			organization: "Organisation",
			legal: "Rechtliche Angaben",
			sender: "Absender",
			payments: "Zahlungen",
			venue: "Erster Veranstaltungsort",
			done: "Fertig",
		},
		organization: {
			question: "Wie heißt Ihre Organisation?",
			lead: "Käufer sehen diesen Namen in Ihrem Shop, auf Tickets und in E-Mails.",
			notAdmitted:
				"Mit dieser Adresse kann keine Organisation angelegt werden.",
			alreadyFounded:
				"Mit dieser Adresse wurde bereits eine Organisation angelegt.",
			unavailable:
				"Organisationen können gerade nicht angelegt werden. Bitte versuchen Sie es später erneut.",
			failed:
				"Die Organisation konnte nicht angelegt werden. Bitte versuchen Sie es erneut.",
			brandNotSaved:
				"Ihre Organisation wurde angelegt, ihr Markenauftritt aber nicht gespeichert. Legen Sie ihn in den Einstellungen fest.",
			brandNoAnswer:
				"Ihre Organisation wurde angelegt. Ihr Markenauftritt ist möglicherweise noch nicht gespeichert – prüfen Sie ihn in den Einstellungen.",
		},
		legal: {
			question: "Wer verkauft die Tickets?",
			lead: "Steht auf jedem Ticket und jeder Rechnung und öffentlich in Ihrem Impressum.",
		},
		sender: {
			question: "Verbinden Sie Ihren E-Mail-Versand",
			lead: "Tickets und Rechnungen werden von Ihrer eigenen Adresse über Ihr Lettermint-Konto versendet.",
			platform: "Vorerst den E-Mail-Versand der Plattform nutzen",
		},
		payments: {
			notConfigured:
				"Zahlungsbenachrichtigungen haben noch keine öffentliche Adresse. Starten Sie den lokalen Tunnel und speichern Sie erneut.",
			platform: "Testkonto der Plattform (Standard)",
			platformNote:
				"Keine echte Zahlung. Verbinden Sie Ihr Live-Konto, um echte Zahlungen anzunehmen.",
			question: "Wie zahlen Ihre Käufer?",
			lead: "Der Checkout läuft über Ihr Revolut-Merchant-Konto.",
			sandbox: "Ihre Sandbox",
			live: "Live",
			sandboxNote:
				"Testbestellungen mit Ihren Revolut-Sandbox-Schlüsseln. Es fließt kein Geld, bis Sie in den Einstellungen auf Live umstellen.",
			liveNote:
				"Echte Bestellungen, ausgezahlt auf Ihr Revolut-Merchant-Konto.",
		},
		venue: {
			question: "Wo findet Ihre erste Veranstaltung statt?",
			lead: "Käufer finden diese Adresse auf ihren Tickets und in ihrem Kalender.",
			add: "Veranstaltungsort hinzufügen",
			edit: "Bearbeiten",
		},
		done: {
			notReady: "{{name}} kann noch nicht verkaufen.",
			checkoutClosed:
				"Der Checkout bleibt geschlossen, bis eine Zahlungsumgebung aktiviert ist.",
			platformSandbox:
				"Testkonto der Plattform (Standard) · keine echte Zahlung. Verbinden Sie in den Einstellungen Ihr Live-Konto von Revolut, um echte Zahlungen anzunehmen.",
			reading: "Verbindungen werden geprüft",
			ready: "{{name}} ist eingerichtet.",
			sandbox:
				"Der Checkout nimmt Testzahlungen an, bis Sie Revolut in den Einstellungen auf Live umstellen.",
			platformMail:
				"E-Mails gehen von {{sender}} über das E-Mail-Konto der Plattform raus, bis Sie in den Einstellungen Ihr eigenes Lettermint-Konto verbinden.",
			createEvent: "Erste Veranstaltung anlegen",
		},
	},
	nav: {
		events: "Events",
		team: "Team",
		locations: "Locations",
		ticketTemplates: "Ticket-Vorlagen",
		integrations: "Integrationen",
		settings: "Einstellungen",
		switchOrganization: "{{name}} — Organisation wechseln",
		accountAndSignOut: "{{name}} — Konto und Abmelden",
		preferences: "Voreinstellungen",
		brandPreferences: "{{brand}} — Voreinstellungen",
		account: "Konto",
		signOut: "Abmelden",
		signOutConfirmTitle: "Abmelden?",
		signOutConfirmMessage: "Sie müssen sich danach erneut anmelden.",
		signOutFailed: "Abmelden fehlgeschlagen — bitte versuchen Sie es erneut.",
		invitationsWaitingOne: "eine Einladung wartet",
		invitationsWaitingOther: "{{count}} Einladungen warten",
		shop: "Shop",
	},
	licence: {
		banner: {
			graceTitle: "Der Ticketverkauf endet am {{stops}}",
			graceBody: "Verlängern Sie die Lizenz bei Revia, um weiter zu verkaufen.",
			readOnlyTitle: "Der Ticketverkauf ist pausiert",
			// "Revia" names no channel yet: an owner fact still to supply.
			readOnlyBody:
				"Bestellungen, Tickets, Rechnungen und Check-in funktionieren weiter. Wenden Sie sich zur Verlängerung an Revia.",
		},
	},
	shell: {
		noAccessTitle: "Noch kein Zugriff",
		noAccessBody:
			"Ihre Rolle schaltet noch keinen Verwaltungsbereich frei. Bitten Sie einen Administrator um eine passende Rolle.",
	},
};
